import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 260 — Sport-aware metric labels (real config, no ML)
import { SportContextHeader } from '@/components/sport/SportContextHeader';
// Phase 185.e — popup-blocker-proof signed view links
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { downloadDocument } from '@/lib/downloadDocument';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { RelationalSelect } from '@/components/RelationalSelect';
import StudentAIRecommendationCard from '@/components/StudentAIRecommendationCard';
import BulkOnboardModal from '@/components/onboarding/BulkOnboardModal';
import QuickResendCredentialsDialog from '@/components/onboarding/QuickResendCredentialsDialog';
// Phase 323 — Self-Signup QR + Batch Attendance QR
import SelfSignupQRDialog from '@/components/onboarding/SelfSignupQRDialog';
import AttendanceSessionQRDialog from '@/components/onboarding/AttendanceSessionQRDialog';
// Phase 325d — Bulk-set base_fee
import BulkSetFeeDialog from '@/components/students/BulkSetFeeDialog';
import MergeDuplicatesDialog from '@/components/students/MergeDuplicatesDialog';
// Phase 197.b — Longitudinal Trend AI growth-trajectory card
import AthleteTrajectoryCard from '@/components/AthleteTrajectoryCard';
import { getSportMatrix, AVAILABLE_SPORTS_LIST } from '@/lib/sportsMetrics';
import { DocumentComposer } from '@/components/documents/DocumentComposer';
import { DocumentLedger } from '@/components/documents/DocumentLedger';
// Phase 248 — Shared form hydrators (controlled/uncontrolled input safety net).
import { hydrateStudentForm } from '@/utils/formHydrators';
import {
  UserPlus, Search, MoreHorizontal, Edit, Trash2, CalendarCheck, IndianRupee,
  Users, Filter, CheckCircle, XCircle, Clock, Download, Star, Award, TrendingUp, AlertTriangle, Sparkles,
  Key, UserCheck, UserX, Share2, Eye, CreditCard, RotateCcw, Loader2, FileText, Banknote, Receipt, FileSpreadsheet, Send, MoreVertical, BellRing, Mail, MessageCircle, QrCode, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import { bestPhotoFor } from '@/lib/photo';
// Phase 278 — Precision-safe compact INR formatter (fixes ₹9,500 → "10K" bug)
import { formatINRCompact } from '@/lib/inrCompact';
// Phase 281 — Context-aware Email Document dialog (fee invoice magic-link share)
import EmailDocumentDialog from '@/components/documents/EmailDocumentDialog';
import { Avatar } from '@/components/Avatar';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { DigitalAthleteCard } from '@/components/DigitalAthleteCard';
// Phase 206 — AI Financial Strategy Co-Pilot widget (lives inside the Fee & Scholarship card)
import SmartDiscountSuggester from '@/components/students/SmartDiscountSuggester';

const courseOptions = ['Football', 'Cricket', 'Badminton', 'Swimming', 'Tennis', 'Basketball', 'Hockey', 'Athletics'];
const batchOptions = ['Morning', 'Afternoon', 'Evening', 'Weekend'];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

// StudentForm defined OUTSIDE the component to prevent re-mounting on state changes
const StudentForm = ({ data, setData, onSubmit, title, submitLabel, courses = [] }) => {
  // Phase 245 — Operational Fee & Scholarship Ingestion. When the operator picks
  // a course, snapshot its base fee onto the student doc so finance always has a
  // deterministic per-student amount even if the course catalog price changes later.
  // The user can override the base fee inline, and the Final Fee is live-computed.
  const baseFeeNum = parseFloat(data.base_fee);
  const scholarshipNum = parseFloat(data.scholarship_amount);
  const safeBase = Number.isFinite(baseFeeNum) ? baseFeeNum : 0;
  const safeScholar = Number.isFinite(scholarshipNum) ? scholarshipNum : 0;
  const finalFee = Math.max(0, Math.round((safeBase - safeScholar) * 100) / 100);
  const formatInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const handleCourseChange = (courseId) => {
    const course = courses.find((c) => c.id === courseId);
    setData((prev) => ({
      ...prev,
      course_id: courseId,
      batch_id: '' /* reset batch on course change */,
      // Auto-snapshot the catalog fee unless the user has already typed an explicit base_fee
      base_fee: course && (prev.base_fee === '' || prev.base_fee === null || prev.base_fee === undefined)
        ? String(course.fees ?? '')
        : (course ? String(course.fees ?? prev.base_fee ?? '') : (prev.base_fee ?? '')),
    }));
  };

  return (
  <form onSubmit={onSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} placeholder="Full name" data-testid="student-name-input" required /></div>
      <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={data.email} onChange={(e) => setData({...data, email: e.target.value})} placeholder="email@example.com" data-testid="student-email-input" required /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Password *</Label><Input type="password" value={data.password || ''} onChange={(e) => setData({...data, password: e.target.value})} placeholder="Min 6 characters" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={data.phone} onChange={(e) => setData({...data, phone: e.target.value})} placeholder="+91 98765..." /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Date of Birth</Label><Input type="date" value={data.dob} onChange={(e) => setData({...data, dob: e.target.value})} /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Course</Label>
        <RelationalSelect
          value={data.course_id || ''}
          onValueChange={handleCourseChange}
          endpoint="/academy/courses"
          entityLabel="Course"
          placeholder="Select course"
          emptyHint="Go to Settings → Courses or quick-add below."
          testid="student-course-select"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Batch</Label>
        <RelationalSelect
          value={data.batch_id || ''}
          onValueChange={(v) => setData((prev) => ({ ...prev, batch_id: v }))}
          endpoint="/academy/batches"
          entityLabel="Batch"
          placeholder={data.course_id ? 'Select batch' : 'Pick a course first'}
          /* Cascade: filter batches by selected course when course is chosen.
             If a batch has no course_id (legacy), show it across courses. */
          filterFn={data.course_id ? (b) => !b.course_id || b.course_id === data.course_id : undefined}
          extraCreatePayload={data.course_id ? { course_id: data.course_id } : {}}
          emptyHint={data.course_id ? 'No batches for this course yet — quick-add below.' : 'No batches configured yet.'}
          testid="student-batch-select"
        />
      </div>
    </div>
    {/* Phase 245 — Per-student financial profile (base fee snapshot + scholarship deduction) */}
    <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 space-y-2.5" data-testid="student-finance-profile">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-emerald-900 tracking-wide">Fee & Scholarship</Label>
        <span className="text-[10px] uppercase text-emerald-700/70">auto-prefilled from course</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Base Fee (₹)</Label>
          <Input
            type="number" min="0" step="0.01"
            value={data.base_fee ?? ''}
            onChange={(e) => setData({ ...data, base_fee: e.target.value })}
            placeholder="0"
            data-testid="student-base-fee-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Scholarship (₹)</Label>
          <Input
            type="number" min="0" step="0.01"
            value={data.scholarship_amount ?? ''}
            onChange={(e) => setData({ ...data, scholarship_amount: e.target.value })}
            placeholder="0"
            data-testid="student-scholarship-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Final Fee</Label>
          <div
            className="h-9 rounded-md border border-emerald-300 bg-white px-3 flex items-center font-semibold text-emerald-900"
            data-testid="student-final-fee-display"
          >
            {formatInr(finalFee)}
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Scholarship Notes</Label>
        <Input
          value={data.scholarship_notes || ''}
          onChange={(e) => setData({ ...data, scholarship_notes: e.target.value })}
          placeholder="Optional — e.g., Sibling discount, Merit, etc."
          data-testid="student-scholarship-notes-input"
        />
      </div>
      {/* Phase 206 — Smart Discount Co-Pilot. Auto-fires whenever base_fee or
          guardian_phone settle. Apply button writes the recommended value
          straight into scholarship_amount, then the live Final Fee math above
          picks it up on the next render. */}
      <SmartDiscountSuggester
        baseFee={data.base_fee}
        guardianPhone={data.guardian_phone}
        studentId={data.id}
        onApply={(amt) => setData((prev) => ({
          ...prev,
          scholarship_amount: String(amt),
          scholarship_notes: prev.scholarship_notes || 'AI co-pilot recommendation',
        }))}
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Guardian Name</Label><Input value={data.guardian_name} onChange={(e) => setData({...data, guardian_name: e.target.value})} placeholder="Parent/Guardian" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Guardian Phone</Label><Input value={data.guardian_phone} onChange={(e) => setData({...data, guardian_phone: e.target.value})} placeholder="+91..." /></div>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Height (cm)</Label><Input type="number" value={data.height} onChange={(e) => setData({...data, height: e.target.value})} placeholder="170" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Weight (kg)</Label><Input type="number" value={data.weight} onChange={(e) => setData({...data, weight: e.target.value})} placeholder="65" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Blood Group</Label><Input value={data.blood_group} onChange={(e) => setData({...data, blood_group: e.target.value})} placeholder="B+" /></div>
    </div>
    <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={data.address} onChange={(e) => setData({...data, address: e.target.value})} placeholder="Full address" /></div>
    <Button type="submit" className="w-full" data-testid="student-form-submit">{submitLabel}</Button>
  </form>
  );
};

export default function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkOnboard, setShowBulkOnboard] = useState(false);
  const [showMergeDuplicates, setShowMergeDuplicates] = useState(false);
  // Phase 323 — Self-Signup QR + Attendance Session QR
  const [showSelfSignupQR, setShowSelfSignupQR] = useState(false);
  const [showAttendanceQR, setShowAttendanceQR] = useState(false);
  // Phase 325d — Bulk-set base_fee
  const [showBulkSetFee, setShowBulkSetFee] = useState(false);
  // Phase 256c — Quick resend credentials (per-row dropdown action)
  const [quickResendStudent, setQuickResendStudent] = useState(null);
  // Phase 257 — Edit/Delete pending fee bills
  const [editFee, setEditFee] = useState(null);
  const [deleteFee, setDeleteFee] = useState(null);
  const [feeMutating, setFeeMutating] = useState(false);
  // Phase 258 — Manual multichannel fee reminder dialog
  const [reminderFee, setReminderFee] = useState(null);
  // Phase 281 — Email-invoice dialog state.
  const [emailFeeRow, setEmailFeeRow] = useState(null);
  const [reminderChannels, setReminderChannels] = useState({ email: true, whatsapp: true });
  const [reminderSending, setReminderSending] = useState(false);
  // Phase 258b — Bulk Reminders super-cell
  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);
  const [bulkChannels, setBulkChannels] = useState({ email: true, whatsapp: true });
  const [bulkOnlyOverdue, setBulkOnlyOverdue] = useState(false);
  const [bulkBatch, setBulkBatch] = useState('');
  const [bulkSport, setBulkSport] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);  // {matched_count, capped_count}
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);  // dispatch result
  const [editStudent, setEditStudent] = useState(null);

  // Phase 248 — Use the shared hydrator from utils/formHydrators so the
  // controlled-input safety net stays consistent across modals (Employee,
  // Honors, etc.). Kept as a local alias for backwards compatibility with
  // the existing onClick={() => setEditStudent(_editFormShape(student))} call
  // sites — refactoring those sites is unnecessary churn for an identical fn.
  const _editFormShape = hydrateStudentForm;
  const [viewStudent, setViewStudent] = useState(null);
  const [studentHistory, setStudentHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Phase 292 — Track the in-flight student id so a slow response for a
  // previously-clicked student never overwrites the modal of a newer one.
  const activeHistoryStudentIdRef = useRef(null);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [calendarStudent, setCalendarStudent] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date().toISOString().slice(0, 7));
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateStatusDialog, setShowDateStatusDialog] = useState(false);
  const [dateDialogStatus, setDateDialogStatus] = useState('present');
  const [savingDateStatus, setSavingDateStatus] = useState(false);

  const [resetPwStudent, setResetPwStudent] = useState(null);
  const [resetPwForm, setResetPwForm] = useState({ password: '', confirm: '' });

  // Phase 260 — derive the dominant sport across the visible roster so the
  // SportContextHeader (and any downstream sport-aware widget) reflects what
  // the academy actually coaches. Ties broken by alphabetical order.
  const dominantSport = useMemo(() => {
    if (!students || students.length === 0) return 'football';
    const counts = {};
    for (const s of students) {
      const sk = (s.sport || s.course || '').toLowerCase().trim();
      if (sk) counts[sk] = (counts[sk] || 0) + 1;
    }
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return ranked.length ? ranked[0][0] : 'football';
  }, [students]);
  // Phase 95 — 2-step credential reset & share flow
  const [resetCompleted, setResetCompleted] = useState(false);
  const [sharingChannel, setSharingChannel] = useState(null);  // 'whatsapp' | 'email' | null
  const [inviteParentStudent, setInviteParentStudent] = useState(null);
  // Phase 101 — Document lifecycle modals (composer + ledger)
  const [composeForStudent, setComposeForStudent] = useState(null);
  const [ledgerForStudent, setLedgerForStudent] = useState(null);
  const [myAcademy, setMyAcademy] = useState(null);
  const [inviteParentForm, setInviteParentForm] = useState({ email: '', name: '', phone: '', password: '' });
  const [invitingParent, setInvitingParent] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [fees, setFees] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [newStudent, setNewStudent] = useState({
    name: '', email: '', phone: '', dob: '', course_id: '', batch_id: '',
    guardian_name: '', guardian_phone: '', blood_group: '', height: '', weight: '', address: '',
    // Phase 245 — per-student financial profile
    base_fee: '', scholarship_amount: '', scholarship_notes: ''
  });
  // Phase 245 — courses cache so StudentForm can auto-snapshot fees on course pick
  const [availableCourses, setAvailableCourses] = useState([]);
  const [feeForm, setFeeForm] = useState({ amount: '', due_date: '', fee_type: 'monthly', description: '' });
  // Phase 289 — Inline "Generate Current Month Fee" inside the per-student modal
  // (fixes empty-fees-tab dead-end + lets owners patch a missing fee without
  // leaving the student profile). Prefilled from the Phase 245 final_fee snapshot.
  const [quickFeeAmount, setQuickFeeAmount] = useState('');
  const [quickFeeSaving, setQuickFeeSaving] = useState(false);
  const [feeSelectedIds, setFeeSelectedIds] = useState([]);
  const [feeSearch, setFeeSearch] = useState('');
  const [feeBatchFilter, setFeeBatchFilter] = useState('');
  // Phase 273 — Fee ledger filter / sort / search toolbar state
  const [feeTableSearch, setFeeTableSearch] = useState('');
  const [feeStatusFilter, setFeeStatusFilter] = useState('all');
  const [feeSortKey, setFeeSortKey] = useState('due_date_desc');
  const [progressCards, setProgressCards] = useState([]);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressForm, setProgressForm] = useState({ student_id: '', skills_rating: {}, comments: '', sport_override: '' });

  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get('/students', { params: { search: search || undefined } });
      setStudents(res.data);
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchFees = useCallback(async () => {
    try {
      const res = await api.get('/fees');
      setFees(res.data);
    } catch (err) {
      console.error('Error fetching fees:', err);
    }
  }, []);

  const fetchAttendance = useCallback(async () => {
    try {
      const res = await api.get('/attendance', { params: { date: attendanceDate } });
      const records = {};
      res.data.forEach(r => { records[r.student_id] = r.status; });
      setAttendanceRecords(records);
    } catch (err) {
      console.error('Error fetching attendance:', err);
    }
  }, [attendanceDate]);

  const fetchProgressCards = useCallback(async () => {
    try {
      const res = await api.get('/progress-cards');
      setProgressCards(res.data);
    } catch (err) {
      console.error('Error fetching progress cards:', err);
    }
  }, []);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);
  useEffect(() => { if (activeTab === 'fees') fetchFees(); }, [activeTab, fetchFees]);
  useEffect(() => { if (activeTab === 'attendance') fetchAttendance(); }, [activeTab, attendanceDate, fetchAttendance]);
  useEffect(() => { if (activeTab === 'progress') fetchProgressCards(); }, [activeTab, fetchProgressCards]);
  useEffect(() => { api.get('/students/analytics').then(r => setAnalytics(r.data)).catch(() => {}); }, [students]);
  // Phase 245 — Cache the course catalog once so the Student modal can look up
  // base fees the instant a course is picked (no extra round-trip per keystroke).
  useEffect(() => {
    api.get('/academy/courses').then(r => setAvailableCourses(r.data || [])).catch(() => {});
  }, []);
  // Phase 101 — Cache academy header for the letterhead.
  useEffect(() => {
    api.get('/academy').then(r => setMyAcademy(r.data)).catch(() => {});
  }, []);

  // Phase 97 — Auto-hydrate Invite Parent modal from the student record so the
  // admin sees the existing guardian's name/phone (and any saved parent email)
  // pre-filled rather than empty fields. Fetches the latest student snapshot
  // when the modal opens to catch any updates made elsewhere in the app.
  useEffect(() => {
    if (!inviteParentStudent) return;
    let cancelled = false;
    (async () => {
      let fresh = inviteParentStudent;
      try {
        const r = await api.get(`/students/${inviteParentStudent.id}`);
        if (!cancelled && r.data) fresh = r.data;
      } catch (e) { /* fall back to the row-snapshot we already have */ }
      if (cancelled) return;
      setInviteParentForm({
        // Prefer an explicit guardian_email if present, otherwise leave blank
        // so the operator can type the parent's address.
        email: fresh.guardian_email || '',
        name: fresh.guardian_name || '',
        phone: fresh.guardian_phone || fresh.emergency_contact || '',
        password: '',
      });
    })();
    return () => { cancelled = true; };
  }, [inviteParentStudent]);

  // Calendar view data
  useEffect(() => {
    if (calendarStudent && calendarMonth && activeTab === 'attendance') {
      api.get('/attendance', { params: { student_id: calendarStudent, month: calendarMonth } })
        .then(r => setCalendarData(r.data || []))
        .catch(() => setCalendarData([]));
    }
  }, [calendarStudent, calendarMonth, activeTab]);

  const handleAddProgressCard = async (e) => {
    e.preventDefault();
    if (!progressForm.student_id) { toast.error('Select a student'); return; }
    try {
      // Phase 98 — Forward whichever skill IDs the user actually rated, regardless of
      // which sport's matrix they came from. This lets the backend store sport-agnostic
      // {skill_id: rating} dictionaries without coupling to a specific schema.
      const skills = {};
      Object.entries(progressForm.skills_rating || {}).forEach(([id, val]) => {
        const num = parseFloat(val);
        if (!Number.isNaN(num)) skills[id] = num;
      });
      await api.post('/progress-cards', { student_id: progressForm.student_id, skills_rating: skills, comments: progressForm.comments });
      toast.success('Progress card created');
      setShowProgressDialog(false);
      setProgressForm({ student_id: '', skills_rating: {}, comments: '', sport_override: '' });
      fetchProgressCards();
    } catch (err) { apiErrorToast(err, 'Failed to create progress card'); }
  };

  const handleViewStudent = async (student) => {
    setViewStudent(student);
    // Phase 292 — Reset stale history BEFORE the fetch fires so the modal
    // never flashes the previous student's fees/attendance while loading.
    // Combined with the activeHistoryStudentIdRef guard below, this also
    // prevents a race where a slow response for student A arriving after
    // student B was clicked overwrites B's data with A's.
    setStudentHistory(null);
    activeHistoryStudentIdRef.current = student?.id || null;
    setHistoryLoading(true);
    // Phase 289 — Seed the inline quick-fee amount from the student's Phase-245
    // financial profile so owners can mint a "Generate Current Month Fee" in one
    // click. Falls back to base_fee, then blank so the user types it in.
    const seed = student?.final_fee ?? student?.base_fee ?? '';
    setQuickFeeAmount(seed === null || seed === undefined ? '' : String(seed));
    try {
      const res = await api.get(`/students/${student.id}/history`);
      // Stale-response guard: ignore if a newer student has since been opened.
      if (activeHistoryStudentIdRef.current === student.id) {
        setStudentHistory(res.data);
      }
    } catch (err) { console.error(err); }
    finally {
      if (activeHistoryStudentIdRef.current === student.id) {
        setHistoryLoading(false);
      }
    }
  };

  // Phase 289 — Mint a single monthly invoice for the currently-viewed student
  // straight from the empty-state inside the Fees tab of the profile modal.
  // Uses the existing POST /api/fees endpoint (idempotent on student+type+period).
  const handleQuickGenerateFee = async () => {
    if (!viewStudent) return;
    const amt = parseFloat(quickFeeAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid fee amount');
      return;
    }
    // Default due date = last day of the current calendar month (IST/local).
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const due = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
    setQuickFeeSaving(true);
    try {
      await api.post('/fees', {
        student_id: viewStudent.id,
        amount: amt,
        due_date: due,
        fee_type: 'monthly',
        description: `Monthly fee — ${now.toLocaleString('en-IN', { month: 'long', year: 'numeric' })}`,
      });
      toast.success('Fee generated for current month');
      // Refresh the student history so the new row + totals appear instantly.
      const res = await api.get(`/students/${viewStudent.id}/history`);
      setStudentHistory(res.data);
      // Also refresh the page-level fees ledger if it's been mounted.
      if (typeof fetchFees === 'function') fetchFees();
    } catch (err) {
      apiErrorToast(err, 'Failed to generate fee');
    } finally {
      setQuickFeeSaving(false);
    }
  };

  const handleImportCSV = async () => {
    try {
      const lines = importData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const students = lines.slice(1).map(line => {
        const vals = line.split(',');
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
        return obj;
      }).filter(s => s.name);
      const res = await api.post('/students/import-csv', { students });
      toast.success(res.data.message);
      setShowImport(false);
      setImportData('');
      fetchStudents();
    } catch (err) { apiErrorToast(err, 'Failed to import CSV'); }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      // Phase 245 — Coerce the financial inputs to numbers (or omit when blank
      // so the backend can fall back to the course catalog snapshot).
      const baseFee = newStudent.base_fee !== '' && newStudent.base_fee !== null && newStudent.base_fee !== undefined
        ? parseFloat(newStudent.base_fee) : null;
      const scholarship = newStudent.scholarship_amount !== '' && newStudent.scholarship_amount !== null && newStudent.scholarship_amount !== undefined
        ? parseFloat(newStudent.scholarship_amount) : null;
      const payload = {
        ...newStudent,
        height: newStudent.height ? parseFloat(newStudent.height) : null,
        weight: newStudent.weight ? parseFloat(newStudent.weight) : null,
        base_fee: Number.isFinite(baseFee) ? baseFee : null,
        scholarship_amount: Number.isFinite(scholarship) ? scholarship : null,
      };
      const res = await api.post('/students', payload);
      const student = res.data;
      if (student.login_email && student.login_password) {
        toast.success(`Student added! Login: ${student.login_email} / ${student.login_password}`, { duration: 10000 });
      } else {
        toast.success('Student added successfully');
      }
      setShowAddDialog(false);
      setNewStudent({ name: '', email: '', phone: '', dob: '', course_id: '', batch_id: '', guardian_name: '', guardian_phone: '', blood_group: '', height: '', weight: '', address: '', base_fee: '', scholarship_amount: '', scholarship_notes: '' });
      fetchStudents();
    } catch (err) {
      // Phase 286.d — Detect the email-typo 422 and prompt "Did you mean X?"
      // The structured detail object carries `code`, `suggested`, and the
      // force-override hint so we can resubmit cleanly with the corrected
      // address (or force-through if the user genuinely owns the typo'd domain).
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 422 &&
        detail && typeof detail === 'object' &&
        detail.code === 'email_typo_suggested'
      ) {
        const useSuggested = window.confirm(
          `Email looks like a typo.\n\n` +
          `${detail.message}\n\n` +
          `OK    → use "${detail.suggested}"\n` +
          `Cancel → keep "${detail.original}" anyway`
        );
        try {
          if (useSuggested) {
            const corrected = { ...payload, email: detail.suggested };
            const res = await api.post('/students', corrected);
            setNewStudent((p) => ({ ...p, email: detail.suggested }));
            toast.success(`Student added with corrected email: ${detail.suggested}`);
            setShowAddDialog(false);
            setNewStudent({ name: '', email: '', phone: '', dob: '', course_id: '', batch_id: '', guardian_name: '', guardian_phone: '', blood_group: '', height: '', weight: '', address: '', base_fee: '', scholarship_amount: '', scholarship_notes: '' });
            fetchStudents();
            return res;
          }
          // Force-through the typo'd address
          const res = await api.post('/students?force_email=true', payload);
          toast.warning(`Student added with un-corrected email: ${detail.original}`);
          setShowAddDialog(false);
          setNewStudent({ name: '', email: '', phone: '', dob: '', course_id: '', batch_id: '', guardian_name: '', guardian_phone: '', blood_group: '', height: '', weight: '', address: '', base_fee: '', scholarship_amount: '', scholarship_notes: '' });
          fetchStudents();
          return res;
        } catch (err2) {
          apiErrorToast(err2, 'Failed to add student');
          return;
        }
      }
      apiErrorToast(err, 'Failed to add student');
    }
  };

  const handleEditStudent = async (e) => {
    e.preventDefault();
    try {
      // Phase 245 — Mirror the coercion used in add so PUT also lands clean numbers.
      const baseFee = editStudent.base_fee !== '' && editStudent.base_fee !== null && editStudent.base_fee !== undefined
        ? parseFloat(editStudent.base_fee) : null;
      const scholarship = editStudent.scholarship_amount !== '' && editStudent.scholarship_amount !== null && editStudent.scholarship_amount !== undefined
        ? parseFloat(editStudent.scholarship_amount) : null;
      const payload = {
        ...editStudent,
        height: editStudent.height ? parseFloat(editStudent.height) : null,
        weight: editStudent.weight ? parseFloat(editStudent.weight) : null,
        base_fee: Number.isFinite(baseFee) ? baseFee : null,
        scholarship_amount: Number.isFinite(scholarship) ? scholarship : null,
      };
      await api.put(`/students/${editStudent.id}`, payload);
      toast.success('Student updated');
      setEditStudent(null);
      fetchStudents();
    } catch (err) {
      apiErrorToast(err, 'Failed to update student');
    }
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Delete this student?')) return;
    try { await api.delete(`/students/${id}`); toast.success('Student deleted'); fetchStudents(); }
    catch (err) { apiErrorToast(err, 'Failed to delete student'); }
  };

  const handleToggleStatus = async (student) => {
    const newStatus = student.status === 'active' ? 'suspended' : 'active';
    try {
      await api.put(`/students/${student.id}`, { status: newStatus });
      toast.success(`Student ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchStudents();
    } catch (err) { apiErrorToast(err, 'Failed to toggle student status'); }
  };

  // Phase 95 — Step 1 of the 2-step Reset & Share flow.
  // Resets the password + copies plaintext to clipboard. Keeps the modal open
  // so the admin can EXPLICITLY choose how to share (WhatsApp / Email) instead
  // of the previous silent-success-and-close pattern.
  const handleResetAndCopy = async () => {
    if (!resetPwStudent) return;
    if (resetPwForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (resetPwForm.password !== resetPwForm.confirm) { toast.error('Passwords do not match'); return; }
    try {
      const r = await api.post(`/students/${resetPwStudent.id}/reset-password`, {
        password: resetPwForm.password,
      });
      // Copy to clipboard (best-effort — fallback toast if blocked).
      let copied = false;
      try {
        await navigator.clipboard.writeText(resetPwForm.password);
        copied = true;
      } catch (_) { /* clipboard API may be unavailable on non-HTTPS dev */ }
      setResetCompleted(true);
      toast.success(
        copied
          ? 'Password reset · copied to clipboard. Now choose how to share with the student.'
          : (r.data?.message || 'Password reset. Now choose how to share with the student.'),
        { duration: 5000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Phase 95 — Step 2 of the 2-step Reset & Share flow.
  // Explicitly dispatches the credentials over the chosen channel.
  const handleShareCredentials = async (channel) => {
    if (!resetPwStudent || !resetCompleted) return;
    setSharingChannel(channel);
    try {
      const r = await api.post(`/students/${resetPwStudent.id}/share-credentials`, {
        password: resetPwForm.password,
        channel,
      });
      toast.success(
        channel === 'whatsapp'
          ? `WhatsApp sent to ${r.data?.to || 'student phone'}`
          : `Email sent to ${r.data?.to || 'student email'}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || `${channel === 'whatsapp' ? 'WhatsApp' : 'Email'} dispatch failed`);
    } finally {
      setSharingChannel(null);
    }
  };

  // Legacy alias kept so the existing UI compiles. New 2-step flow uses
  // handleResetAndCopy + handleShareCredentials.
  const handleResetPassword = handleResetAndCopy;

  const handleInviteParent = async () => {
    if (!inviteParentStudent) return;
    if (!inviteParentForm.email) { toast.error('Parent email required'); return; }
    setInvitingParent(true);
    try {
      const res = await api.post(`/students/${inviteParentStudent.id}/invite-parent`, {
        email: inviteParentForm.email,
        name: inviteParentForm.name || inviteParentStudent.guardian_name || 'Parent',
        phone: inviteParentForm.phone || inviteParentStudent.guardian_phone || '',
        password: inviteParentForm.password || 'Parent@1234',
      });
      toast.success(`Parent invited! Email: ${res.data.email} / Temp password: ${res.data.temp_password}`, { duration: 12000 });
      setInviteParentStudent(null);
      setInviteParentForm({ email: '', name: '', phone: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite parent');
    } finally {
      setInvitingParent(false);
    }
  };

  const handleCalendarDateClick = (date, currentStatus) => {
    setSelectedDate(date);
    setDateDialogStatus(currentStatus || 'present');
    setShowDateStatusDialog(true);
  };

  const handleSaveDateStatus = async () => {
    if (!selectedDate || !calendarStudent) return;
    setSavingDateStatus(true);
    try {
      await api.post('/attendance', {
        student_id: calendarStudent,
        date: selectedDate,
        status: dateDialogStatus
      });
      toast.success(`Marked as ${dateDialogStatus} for ${selectedDate}`);
      // Refresh calendar data
      const res = await api.get('/attendance', {
        params: { student_id: calendarStudent, month: calendarMonth }
      });
      setCalendarData(res.data || []);
      setShowDateStatusDialog(false);
    } catch (err) {
      apiErrorToast(err, 'Failed to update attendance');
    } finally {
      setSavingDateStatus(false);
    }
  };

  const refreshCalendarForStudent = useCallback(async (studentId) => {
    if (!studentId || calendarStudent !== studentId || !calendarMonth) return;
    try {
      const res = await api.get('/attendance', { params: { student_id: studentId, month: calendarMonth } });
      setCalendarData(res.data || []);
    } catch (err) {
      console.error('Calendar refresh failed:', err);
    }
  }, [calendarStudent, calendarMonth]);

  // Phase 65.5 Smart-Sync — refresh KPIs + broadcast change for any listening view
  const broadcastAttendanceChange = useCallback(async () => {
    try {
      const r = await api.get('/students/analytics');
      setAnalytics(r.data);
    } catch (err) { /* non-blocking — KPIs stay stale on transient failure */ }
    window.dispatchEvent(new CustomEvent('outplay:attendance-changed'));
  }, []);

  const handleMarkAttendance = async (studentId, status) => {
    // Optimistic UI update — calendar grid + table both reflect immediately
    setAttendanceRecords(prev => ({ ...prev, [studentId]: status }));
    if (calendarStudent === studentId && calendarMonth && attendanceDate.startsWith(calendarMonth)) {
      setCalendarData(prev => {
        const without = (prev || []).filter(r => r.date !== attendanceDate);
        return [...without, { date: attendanceDate, status, student_id: studentId }];
      });
    }
    try {
      await api.post('/attendance', { student_id: studentId, date: attendanceDate, status });
      toast.success(`Marked ${status}`);
      refreshCalendarForStudent(studentId);
      broadcastAttendanceChange();
    } catch (err) {
      // Revert optimistic update
      setAttendanceRecords(prev => { const n = { ...prev }; delete n[studentId]; return n; });
      refreshCalendarForStudent(studentId);
      apiErrorToast(err, 'Failed to mark attendance');
    }
  };

  const handleBulkAttendance = async (status) => {
    const records = students.filter(s => s.status === 'active').map(s => ({ student_id: s.id, status }));
    try {
      await api.post('/attendance/bulk', { date: attendanceDate, records });
      const newRecords = {};
      records.forEach(r => { newRecords[r.student_id] = r.status; });
      setAttendanceRecords(newRecords);
      if (calendarStudent) refreshCalendarForStudent(calendarStudent);
      broadcastAttendanceChange();
      toast.success(`Marked all students as ${status}`);
    } catch (err) {
      apiErrorToast(err, 'Failed to bulk-mark attendance');
    }
  };

  const [generatingFees, setGeneratingFees] = useState(false);
  const handleGenerateFees = async (e) => {
    e.preventDefault();
    if (generatingFees) return; // Phase 65.6 — prevent double-click double-bill
    const ids = feeSelectedIds.length > 0 ? feeSelectedIds : students.filter(s => s.status === 'active').map(s => s.id);
    setGeneratingFees(true);
    try {
      const r = await api.post('/fees/bulk-generate', {
        student_ids: ids, amount: parseFloat(feeForm.amount),
        due_date: feeForm.due_date, fee_type: feeForm.fee_type, description: feeForm.description
      });
      const created = r.data?.count ?? ids.length;
      const skipped = r.data?.skipped_count ?? 0;
      const msg = skipped > 0
        ? `Fees generated for ${created} students · ${skipped} skipped (already billed for this period)`
        : `Fees generated for ${created} students`;
      toast.success(msg);
      setShowFeeDialog(false);
      setFeeForm({ amount: '', due_date: '', fee_type: 'monthly', description: '' });
      setFeeSelectedIds([]);
      setFeeSearch('');
      setFeeBatchFilter('');
      fetchFees();
    } catch (err) {
      // "Truth in logic" — always re-sync from server before reporting failure,
      // because a transient timeout on the email hop (not the DB insert) would
      // otherwise produce a false-negative toast while the records exist in DB.
      fetchFees();
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (err?.code === 'ECONNABORTED' || status === 504 || !err?.response) {
        toast.warning('Request took longer than expected — fee records have been refreshed. Please verify the new invoices appear below.');
      } else {
        toast.error(detail || 'Failed to generate fees');
      }
    } finally {
      setGeneratingFees(false);
    }
  };

  // Phase 202.iso-date — "Mark Paid" now opens an interactive modal that
  // captures payment metadata (mode, amount, date, transaction ref) so the
  // academy ledger isn't just `payment_method='manual'` with zero context.
  // Backend already accepts these fields on PUT /api/fees/{id} (FeeUpdate
  // has status / payment_method / payment_date / transaction_id).
  const handlePayFee = (fee) => {
    setOfflinePayFee(fee);
    setOfflinePayForm({
      mode: 'Cash',
      amount: String(fee?.amount || ''),
      payment_date: new Date().toISOString().split('T')[0],
      transaction_id: '',
    });
  };

  const handleConfirmOfflinePayment = async (e) => {
    e?.preventDefault?.();
    if (!offlinePayFee?.id) return;
    setSavingOfflinePay(true);
    try {
      await api.put(`/fees/${offlinePayFee.id}`, {
        status: 'paid',
        payment_method: offlinePayForm.mode || 'manual',
        payment_date: offlinePayForm.payment_date,
        transaction_id: (offlinePayForm.transaction_id || '').trim() || null,
      });
      toast.success(`₹${offlinePayForm.amount} settled · ${offlinePayForm.mode}`);
      setOfflinePayFee(null);
      fetchFees();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingOfflinePay(false);
    }
  };

  const [payingFee, setPayingFee] = useState(null);
  const [showRazorpayGuide, setShowRazorpayGuide] = useState(false);
  const [refundFee, setRefundFee] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '', offline: false, offline_method: 'cash_or_upi' });
  const [processingRefund, setProcessingRefund] = useState(false);
  // Phase 202.iso-date — Record Offline Payment modal state
  const [offlinePayFee, setOfflinePayFee] = useState(null);
  const [offlinePayForm, setOfflinePayForm] = useState({
    mode: 'Cash', amount: '', payment_date: '', transaction_id: ''
  });
  const [savingOfflinePay, setSavingOfflinePay] = useState(false);

  const handlePayFeeOnline = async (fee) => {
    setPayingFee(fee.id);
    try {
      // Create order using academy's Razorpay keys
      const orderRes = await api.post(`/fees/${fee.id}/create-order`);
      const order = orderRes.data;
      
      // If already paid
      if (order.status === 'paid') {
        toast.info('Fee already paid!');
        fetchFees();
        setPayingFee(null);
        return;
      }
      
      // Payment gateway not configured - show setup guide
      if (order.mock_mode) {
        setPayingFee(null);
        setShowRazorpayGuide(true);
        return;
      }
      
      // Real Razorpay checkout
      if (!window.Razorpay) { 
        // Load Razorpay script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => { script.onload = resolve; setTimeout(resolve, 3000); });
      }

      if (!window.Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh and try again.'); 
        setPayingFee(null); 
        return; 
      }
      
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.order_id,
        name: order.academy_name || 'Academy',
        description: order.description || `Fee Payment - ₹${(order.amount / 100).toLocaleString()}`,
        prefill: {
          name: fee.student_name || '',
          email: '',
          contact: ''
        },
        handler: async (response) => {
          try {
            await api.post(`/fees/${fee.id}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              payment_method: 'razorpay'
            });
            toast.success('✅ Payment successful! Receipt sent.');
            fetchFees();
          } catch (err) {
            toast.error('Payment verification failed.');
          }
          setPayingFee(null);
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled');
            setPayingFee(null);
          }
        },
        theme: { color: '#0f766e' }
      });
      
      rzp.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description || 'Please try again'}`);
        setPayingFee(null);
      });
      
      rzp.open();
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed.');
    }
    setPayingFee(null);
  };

  const handleInitiateRefund = async (e) => {
    e.preventDefault();
    if (!refundForm.amount || parseFloat(refundForm.amount) <= 0) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    if (parseFloat(refundForm.amount) > refundFee.amount) {
      toast.error('Refund amount cannot exceed fee amount');
      return;
    }
    
    setProcessingRefund(true);
    try {
      const res = await api.post(`/fees/${refundFee.id}/refund`, {
        amount: parseFloat(refundForm.amount),
        reason: refundForm.reason || 'Requested by academy',
        ...(refundForm.offline ? { offline: true, offline_method: refundForm.offline_method } : {}),
      });
      toast.success(res.data.message || 'Refund initiated successfully!');
      setRefundFee(null);
      setRefundForm({ amount: '', reason: '', offline: false, offline_method: 'cash_or_upi' });
      fetchFees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    } finally {
      setProcessingRefund(false);
    }
  };


// Status badges defined OUTSIDE component
const StatusBadge = ({ status }) => {
  const colors = { active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', suspended: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', alumni: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.active}`}>{status}</span>;
};

const FeeStatusBadge = ({ status, refundStatus }) => {
  // Phase 273 — display "refunded" pill when refund_status flag is set, even
  // if the underlying fees.status is still "paid" (which is by design — refund
  // tracking is a separate field so accounting history stays intact).
  const effective = (refundStatus === 'refunded' || refundStatus === 'processing')
    ? 'refunded' : status;
  const colors = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    refunded: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
    draft: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  };
  return <span data-testid={`fee-status-${effective}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[effective] || colors.pending}`}>{effective}</span>;
};

  if (loading) return (
    <div className="space-y-4" data-testid="students-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  // Phase 273 — Derived fee list: applies status filter, fuzzy search on
  // student name + custom_id, and sort (due_date / amount). The badge
  // pill text drives the filter: "refunded" comes from refund_status, the
  // rest from fees.status.
  const displayedFees = (() => {
    const q = (feeTableSearch || '').trim().toLowerCase();
    let rows = (fees || []).map(f => ({
      ...f,
      _effectiveStatus: (f.refund_status === 'refunded' || f.refund_status === 'processing')
        ? 'refunded' : (f.status || 'pending'),
    }));
    if (feeStatusFilter !== 'all') {
      rows = rows.filter(f => f._effectiveStatus === feeStatusFilter);
    }
    if (q) {
      rows = rows.filter(f =>
        (f.student_name || '').toLowerCase().includes(q)
        || (f.custom_id || '').toLowerCase().includes(q)
      );
    }
    const sorters = {
      due_date_desc: (a, b) => (b.due_date || '').localeCompare(a.due_date || ''),
      due_date_asc:  (a, b) => (a.due_date || '').localeCompare(b.due_date || ''),
      amount_desc:   (a, b) => (b.amount || 0) - (a.amount || 0),
      amount_asc:    (a, b) => (a.amount || 0) - (b.amount || 0),
    };
    rows.sort(sorters[feeSortKey] || sorters.due_date_desc);
    return rows;
  })();

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Students</h1>
          <p className="text-sm text-muted-foreground">{students.length} students enrolled</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const res = await api.get('/export/students.csv', { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a');
              link.href = url;
              link.download = 'students.csv';
              link.click();
              window.URL.revokeObjectURL(url);
              toast.success('Students exported!');
            } catch (err) { toast.error('Export failed'); }
          }} data-testid="students-export-csv"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Dialog open={showImport} onOpenChange={setShowImport}>
            <DialogTrigger asChild><Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1 rotate-180" /> Import CSV</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Import Students from CSV</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">CSV Format (headers required):</p>
                  <code className="block bg-background p-2 rounded text-[10px]">name,email,phone,sport,batch,dob,guardian_name</code>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Upload CSV File</Label>
                  <Input type="file" accept=".csv,.txt" className="text-xs" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setImportData(ev.target.result);
                      reader.readAsText(file);
                    }
                  }} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Or paste CSV data</Label>
                  <textarea className="w-full h-28 text-xs font-mono border rounded-lg p-2 bg-muted/50 resize-none" placeholder={"name,email,phone,sport,batch\nRiya Patel,riya@test.com,+91 9876543200,Football,Morning\nArjun Kumar,arjun@test.com,+91 9876543201,Football,Evening"} value={importData} onChange={(e) => setImportData(e.target.value)} />
                </div>
                {importData && <p className="text-xs text-muted-foreground">{importData.trim().split('\n').length - 1} students to import</p>}
                <Button className="w-full" onClick={handleImportCSV} disabled={!importData.trim()}>Import Students</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showFeeDialog} onOpenChange={(o) => { if (!generatingFees) setShowFeeDialog(o); }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="students-generate-fees-button"><IndianRupee className="h-4 w-4 mr-1" /> Generate Fees</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><IndianRupee className="h-5 w-5 text-primary" /> Generate Fees</DialogTitle>
                <DialogDescription className="text-xs">
                  Pick one student, several, or all active. Choose the amount, due date, and fee type — duplicates for the same period are blocked automatically.
                </DialogDescription>
              </DialogHeader>
              <Tabs defaultValue={selectedStudents.length > 0 ? "multiple" : "single"} className="mt-2">
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="single" className="text-xs">Single Student</TabsTrigger>
                  <TabsTrigger value="multiple" className="text-xs">Multiple ({feeSelectedIds.length})</TabsTrigger>
                  <TabsTrigger value="all" className="text-xs">All Active ({students.filter(s => s.status === 'active').length})</TabsTrigger>
                </TabsList>

                {/* Single Student Tab */}
                <TabsContent value="single" className="space-y-3 mt-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Select Student</Label>
                    <Select onValueChange={(v) => setFeeSelectedIds([v])}>
                      <SelectTrigger data-testid="fee-single-student-select"><SelectValue placeholder="Choose a student" /></SelectTrigger>
                      <SelectContent>
                        {students.filter(s => s.status === 'active').map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} — {s.batch || 'No batch'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {feeSelectedIds.length === 1 && <p className="text-xs text-emerald-600 font-medium">1 student selected</p>}
                </TabsContent>

                {/* Multiple Students Tab */}
                <TabsContent value="multiple" className="space-y-3 mt-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search students..." value={feeSearch} onChange={e => setFeeSearch(e.target.value)} className="pl-9 h-9" />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {['All', ...new Set(students.filter(s => s.status === 'active').map(s => s.batch).filter(Boolean))].map(b => (
                      <button key={b} onClick={() => setFeeBatchFilter(b === 'All' ? '' : b)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${(b === 'All' && !feeBatchFilter) || feeBatchFilter === b ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>{b}</button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{feeSelectedIds.length} selected</span>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                        const filtered = students.filter(s => s.status === 'active' && (!feeBatchFilter || s.batch === feeBatchFilter) && (!feeSearch || s.name.toLowerCase().includes(feeSearch.toLowerCase())));
                        setFeeSelectedIds(filtered.map(s => s.id));
                      }}>Select All</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setFeeSelectedIds([])}>Clear</Button>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                    {students.filter(s => s.status === 'active' && (!feeBatchFilter || s.batch === feeBatchFilter) && (!feeSearch || s.name.toLowerCase().includes(feeSearch.toLowerCase()))).map(s => (
                      <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer" data-testid={`fee-student-row-${s.id}`}>
                        <Checkbox checked={feeSelectedIds.includes(s.id)} onCheckedChange={(c) => setFeeSelectedIds(c ? [...feeSelectedIds, s.id] : feeSelectedIds.filter(id => id !== s.id))} data-testid={`fee-student-checkbox-${s.id}`} />
                        <span className="text-sm flex-1">{s.name}</span>
                        {s.batch && <Badge variant="outline" className="text-[10px]">{s.batch}</Badge>}
                        {s.sport && <Badge variant="secondary" className="text-[10px]">{s.sport}</Badge>}
                      </label>
                    ))}
                  </div>
                </TabsContent>

                {/* All Students Tab */}
                <TabsContent value="all" className="space-y-3 mt-3">
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 text-center">
                    <Users className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                    <p className="text-lg font-display font-bold text-emerald-700">{students.filter(s => s.status === 'active').length} Active Students</p>
                    <p className="text-xs text-emerald-600 mt-1">Fees will be generated for all active students</p>
                    {feeForm.amount && (
                      <p className="text-sm font-semibold text-emerald-800 mt-2">
                        Projected: ₹{(parseFloat(feeForm.amount) * students.filter(s => s.status === 'active').length).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {Object.entries(students.filter(s => s.status === 'active').reduce((acc, s) => { acc[s.batch || 'No batch'] = (acc[s.batch || 'No batch'] || 0) + 1; return acc; }, {})).map(([batch, count]) => (
                      <Badge key={batch} variant="outline">{batch}: {count}</Badge>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Common Fee Details */}
              <form onSubmit={handleGenerateFees} className="space-y-3 mt-4 pt-4 border-t border-border">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Amount (₹) *</Label><Input type="number" value={feeForm.amount} onChange={(e) => setFeeForm({...feeForm, amount: e.target.value})} placeholder="5000" required data-testid="fee-amount-input" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Due Date *</Label><Input type="date" value={feeForm.due_date} onChange={(e) => setFeeForm({...feeForm, due_date: e.target.value})} required data-testid="fee-due-date-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Fee Type</Label>
                    <Select value={feeForm.fee_type} onValueChange={(v) => setFeeForm({...feeForm, fee_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="one-time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Description</Label><Input value={feeForm.description} onChange={(e) => setFeeForm({...feeForm, description: e.target.value})} placeholder="Monthly training fee" /></div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <p className="text-xs text-muted-foreground">
                    {feeSelectedIds.length > 0 ? `${feeSelectedIds.length} student${feeSelectedIds.length > 1 ? 's' : ''} selected` : `All ${students.filter(s => s.status === 'active').length} active students`}
                    {feeForm.amount && ` • Total: ₹${((feeSelectedIds.length > 0 ? feeSelectedIds.length : students.filter(s => s.status === 'active').length) * parseFloat(feeForm.amount || 0)).toLocaleString()}`}
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-10"
                  disabled={generatingFees}
                  data-testid="fee-generate-submit"
                >
                  {generatingFees ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
                  ) : (
                    <><IndianRupee className="h-4 w-4 mr-2" /> Generate Fees</>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="students-add-button"><UserPlus className="h-4 w-4 mr-1" /> Add Student</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add New Student</DialogTitle></DialogHeader>
              <StudentForm data={newStudent} setData={setNewStudent} onSubmit={handleAddStudent} submitLabel="Add Student" courses={availableCourses} />
            </DialogContent>
          </Dialog>
          {/* Phase 256 — Bulk Onboard Students */}
          <Button size="sm" variant="outline" onClick={() => setShowBulkOnboard(true)} data-testid="students-bulk-onboard-button">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Bulk Import
          </Button>
          {/* Phase 323 — Self-Signup QR */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowSelfSignupQR(true)}
            data-testid="students-invite-qr-button"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <QrCode className="h-4 w-4 mr-1" /> Invite via QR
          </Button>
          {/* Phase 325d — Bulk-set base_fee */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowBulkSetFee(true)}
            data-testid="students-bulk-set-fee-button"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
          >
            <IndianRupee className="h-4 w-4 mr-1" /> Set Fees
          </Button>
          {/* Phase 326h — Find & merge duplicate students */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowMergeDuplicates(true)}
            data-testid="students-merge-duplicates-button"
            className="border-amber-400 text-amber-900 hover:bg-amber-50"
          >
            <Users className="h-4 w-4 mr-1" /> Merge Duplicates
          </Button>
          <MergeDuplicatesDialog
            open={showMergeDuplicates}
            onOpenChange={setShowMergeDuplicates}
            onMerged={() => fetchStudents?.()}
          />
          <BulkSetFeeDialog
            open={showBulkSetFee}
            onClose={() => setShowBulkSetFee(false)}
            batches={batchOptions}
            onApplied={() => fetchStudents?.()}
          />
          <SelfSignupQRDialog
            open={showSelfSignupQR}
            onClose={() => setShowSelfSignupQR(false)}
            role="student"
          />
          <AttendanceSessionQRDialog
            open={showAttendanceQR}
            onClose={() => setShowAttendanceQR(false)}
            kind="student"
            batches={batchOptions}
          />
          <BulkOnboardModal
            open={showBulkOnboard}
            onClose={() => setShowBulkOnboard(false)}
            role="student"
            onComplete={() => fetchStudents?.()}
          />
          {/* Phase 256c — Quick Resend Credentials dialog (per-row dropdown) */}
          <QuickResendCredentialsDialog
            open={!!quickResendStudent}
            onClose={() => setQuickResendStudent(null)}
            role="student"
            user={quickResendStudent}
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100/70 p-1">
          <TabsTrigger
            value="list"
            data-testid="tab-students-list"
            className="data-[state=active]:bg-sky-100 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm data-[state=active]:border-sky-300 data-[state=active]:border text-slate-600 hover:text-sky-700 transition-colors"
          >
            <Users className="h-4 w-4 mr-1" /> Students
          </TabsTrigger>
          <TabsTrigger
            value="attendance"
            data-testid="tab-attendance"
            className="data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm data-[state=active]:border-emerald-300 data-[state=active]:border text-slate-600 hover:text-emerald-700 transition-colors"
          >
            <CalendarCheck className="h-4 w-4 mr-1" /> Attendance
          </TabsTrigger>
          <TabsTrigger
            value="fees"
            data-testid="tab-fees"
            className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800 data-[state=active]:shadow-sm data-[state=active]:border-amber-300 data-[state=active]:border text-slate-600 hover:text-amber-700 transition-colors"
          >
            <IndianRupee className="h-4 w-4 mr-1" /> Fees
          </TabsTrigger>
          <TabsTrigger
            value="progress"
            data-testid="tab-progress"
            className="data-[state=active]:bg-violet-100 data-[state=active]:text-violet-800 data-[state=active]:shadow-sm data-[state=active]:border-violet-300 data-[state=active]:border text-slate-600 hover:text-violet-700 transition-colors"
          >
            <Award className="h-4 w-4 mr-1" /> Progress
          </TabsTrigger>
        </TabsList>

        {/* AI Insights Cards - Students tab only */}
        {activeTab === 'list' && analytics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1"><CalendarCheck className="h-4 w-4 text-emerald-600" /><Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">This Month</Badge></div>
                <p className="text-xl font-display font-bold tabular-nums text-emerald-700">{analytics.attendance_rate}%</p>
                <p className="text-[10px] text-emerald-600 font-medium">Attendance Rate</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1"><AlertTriangle className="h-4 w-4 text-amber-600" /><Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300">Action</Badge></div>
                <p className="text-xl font-display font-bold tabular-nums text-amber-700">{analytics.low_attendance_count}</p>
                <p className="text-[10px] text-amber-600 font-medium">Low Attendance</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-200 dark:border-red-800 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1"><IndianRupee className="h-4 w-4 text-red-600" /><Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-300">{analytics.collection_rate}%</Badge></div>
                <p className="text-xl font-display font-bold tabular-nums text-red-700">{analytics.overdue_count}</p>
                <p className="text-[10px] text-red-600 font-medium">Fees Overdue</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1"><TrendingUp className="h-4 w-4 text-blue-600" />{analytics.top_student && <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-700 border-blue-300">{analytics.top_student.custom_id}</Badge>}</div>
                <p className="text-xl font-display font-bold tabular-nums text-blue-700">{formatINRCompact(analytics.paid_fees)}</p>
                <p className="text-[10px] text-blue-600 font-medium">Collected</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Students List Tab */}
        <TabsContent value="list">
          <div className="mb-3">
            <SportContextHeader sport={dominantSport} compact={false} />
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 p-3 border-b border-border flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search by name, ID, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="students-search-input" />
                </div>
                <Select value={quickFilter} onValueChange={setQuickFilter}>
                  <SelectTrigger className="w-40 h-9"><SelectValue placeholder="Filter by batch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Batches</SelectItem>
                    {batchOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"><Checkbox onCheckedChange={(c) => setSelectedStudents(c ? students.map(s => s.id) : [])} /></TableHead>
                      <TableHead className="w-24">ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="whitespace-nowrap">Sport</TableHead>
                      <TableHead className="whitespace-nowrap">Batch</TableHead>
                      <TableHead className="whitespace-nowrap">Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="whitespace-nowrap">BMI</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No students yet. Add your first student to get started.</TableCell></TableRow>
                    ) : students.map((student) => (
                      <TableRow key={student.id} data-testid={`student-row-${student.id}`}>
                        <TableCell><Checkbox checked={selectedStudents.includes(student.id)} onCheckedChange={(c) => setSelectedStudents(c ? [...selectedStudents, student.id] : selectedStudents.filter(id => id !== student.id))} /></TableCell>
                        <TableCell><span className="text-xs font-mono text-muted-foreground">{student.custom_id || '-'}</span></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <StudentAvatar student={student} size={32} />
                            <div className="min-w-0">
                              <p className="font-medium text-sm cursor-pointer hover:text-primary hover:underline truncate" onClick={() => handleViewStudent(student)}>{student.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{student.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap"><Badge variant="outline" className="text-xs">{student.sport || '-'}</Badge></TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{student.batch || '-'}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{student.phone || '-'}</TableCell>
                        <TableCell><StatusBadge status={student.status} /></TableCell>
                        <TableCell className="whitespace-nowrap text-sm tabular-nums">{student.bmi || '-'}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid="table-row-actions-menu"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setEditStudent(_editFormShape(student))} data-testid={`edit-student-${student.id}`}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setResetPwStudent(student)}><Key className="h-4 w-4 mr-2" /> Reset Password</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setQuickResendStudent(student)} data-testid={`resend-credentials-${student.id}`}><Send className="h-4 w-4 mr-2" /> Resend Credentials</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setInviteParentStudent(student)} data-testid={`invite-parent-${student.id}`}><UserPlus className="h-4 w-4 mr-2" /> Invite Parent</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {/* Phase 101 — Document Lifecycle */}
                              <DropdownMenuItem onClick={() => setComposeForStudent(student)} data-testid={`compose-doc-${student.id}`}><FileText className="h-4 w-4 mr-2" /> Issue Document…</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setLedgerForStudent(student)} data-testid={`ledger-${student.id}`}><FileText className="h-4 w-4 mr-2 opacity-60" /> Issued Documents</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleToggleStatus(student)}>
                                {student.status === 'active' ? <><UserX className="h-4 w-4 mr-2" /> Deactivate</> : <><UserCheck className="h-4 w-4 mr-2" /> Activate</>}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteStudent(student.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <div className="space-y-4">
            {/* Selectors Row */}
            <Card>
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="flex items-center gap-2 flex-1 flex-wrap">
                    <Input type="month" value={calendarMonth} onChange={(e) => setCalendarMonth(e.target.value)} className="w-40 h-9 text-xs" />
                    <Select value={calendarStudent} onValueChange={setCalendarStudent}>
                      <SelectTrigger className="w-48 h-9 text-xs"><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>{students.filter(s => s.status === 'active').map(s => <SelectItem key={s.id} value={s.id}>{s.custom_id} - {s.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select onValueChange={(v) => { if (v !== 'all') { const filtered = students.filter(s => s.batch === v); if (filtered.length > 0) setCalendarStudent(filtered[0].id); } }}>
                      <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Batch" /></SelectTrigger>
                      <SelectContent>{batchOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {/* Phase 323 — Open Attendance QR */}
                  <Button
                    size="sm"
                    onClick={() => setShowAttendanceQR(true)}
                    data-testid="students-open-attendance-qr"
                    className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
                  >
                    <ScanLine className="h-4 w-4 mr-1" /> Open Attendance QR
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Calendar View (when student selected) */}
            {calendarStudent && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{students.find(s => s.id === calendarStudent)?.name} — {calendarMonth}</CardTitle>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Present</span>
                      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-500" /> Absent</span>
                      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> Late</span>
                      <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-muted" /> No data</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const year = parseInt(calendarMonth.split('-')[0]);
                    const month = parseInt(calendarMonth.split('-')[1]) - 1;
                    const daysInMonth = new Date(year, month + 1, 0).getDate();
                    const firstDay = new Date(year, month, 1).getDay();
                    const calMap = {};
                    calendarData.forEach(r => { calMap[r.date] = r.status; });
                    const present = Object.values(calMap).filter(s => s === 'present').length;
                    const total = Object.keys(calMap).length;
                    const rate = total > 0 ? Math.round(present / total * 100) : 0;
                    
                    return (
                      <div className="space-y-3">
                        <div className="flex gap-2 text-sm flex-wrap">
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                            {present} Present
                          </Badge>
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-red-500" />
                            {Object.values(calMap).filter(s => s === 'absent').length} Absent
                          </Badge>
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-amber-500" />
                            {Object.values(calMap).filter(s => s === 'late').length} Late
                          </Badge>
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-slate-400" />
                            {Object.values(calMap).filter(s => s === 'leave').length} Leave
                          </Badge>
                          <Badge className={`text-xs ${rate >= 85 ? 'bg-emerald-600' : rate >= 70 ? 'bg-amber-600' : 'bg-red-600'}`}>
                            {rate}% rate
                          </Badge>
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                            <div key={d} className="text-center text-[10px] font-medium text-muted-foreground py-1">{d}</div>
                          ))}
                          {Array.from({length: firstDay}).map((_, i) => <div key={`e${i}`} />)}
                          {Array.from({length: daysInMonth}).map((_, i) => {
                            const day = i + 1;
                            const dateStr = `${calendarMonth}-${String(day).padStart(2, '0')}`;
                            const status = calMap[dateStr];
                            const bg = status === 'present' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-300' : 
                                       status === 'absent' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-300' : 
                                       status === 'late' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300' :
                                       status === 'leave' ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 border-slate-400' :
                                       'bg-muted/30 text-muted-foreground border-transparent';
                            return (
                              <button 
                                key={day} 
                                onClick={() => handleCalendarDateClick(dateStr, status)} 
                                title={`${dateStr}: ${status || 'Click to mark'}`}
                                className={`h-9 rounded-md border text-xs font-medium flex items-center justify-center transition-all duration-150 hover:ring-2 hover:ring-primary hover:scale-105 cursor-pointer ${bg}`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Bulk Mark Table */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Quick Mark — {formatDate(attendanceDate)}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} className="w-auto h-8 text-xs" />
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleBulkAttendance('present')}><CheckCircle className="h-3.5 w-3.5 mr-1" /> All Present</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Student</TableHead><TableHead className="whitespace-nowrap">Batch</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {students.filter(s => s.status === 'active').map((student) => (
                      <TableRow key={student.id} className={attendanceRecords[student.id] === 'present' ? 'animate-flash' : ''}>
                        <TableCell><span className="font-medium text-sm">{student.name}</span></TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{student.batch || '-'}</TableCell>
                        <TableCell>
                          {attendanceRecords[student.id] ? (
                            <Badge variant={attendanceRecords[student.id] === 'present' ? 'default' : attendanceRecords[student.id] === 'late' ? 'secondary' : 'destructive'} className="text-xs">{attendanceRecords[student.id]}</Badge>
                          ) : <span className="text-xs text-muted-foreground">Not marked</span>}
                        </TableCell>
                        <TableCell>
                          {/* Phase 275 — flex-nowrap guards against the 3 status
                             buttons wrapping into a double line on narrow viewports
                             (Galaxy Z Fold, narrow split-pane). */}
                          <div className="flex flex-nowrap gap-1 whitespace-nowrap">
                            <Button size="sm" variant={attendanceRecords[student.id] === 'present' ? 'default' : 'outline'} className="h-7 text-xs px-2" onClick={() => handleMarkAttendance(student.id, 'present')}><CheckCircle className="h-3 w-3 mr-1" /> P</Button>
                            <Button size="sm" variant={attendanceRecords[student.id] === 'absent' ? 'destructive' : 'outline'} className="h-7 text-xs px-2" onClick={() => handleMarkAttendance(student.id, 'absent')}><XCircle className="h-3 w-3 mr-1" /> A</Button>
                            <Button size="sm" variant={attendanceRecords[student.id] === 'late' ? 'secondary' : 'outline'} className="h-7 text-xs px-2" onClick={() => handleMarkAttendance(student.id, 'late')}><Clock className="h-3 w-3 mr-1" /> L</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Fees Tab */}
        <TabsContent value="fees">
          <div className="space-y-4">
            {/* Fee Summary Cards */}
            {analytics && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card data-testid="kpi-collected" className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200">
                  <CardContent className="p-3">
                    <p className="text-xl font-display font-bold tabular-nums text-emerald-700">{formatINRCompact(analytics.paid_fees)}</p>
                    <p className="text-[10px] text-emerald-600 font-medium">Collected</p>
                    {/* Phase 273b — Refund visibility sub-line. Only renders when there's actual refund volume. */}
                    {analytics.refunded_amount > 0 && (
                      <p
                        data-testid="kpi-refund-subline"
                        className="text-[10px] mt-1 text-pink-700 dark:text-pink-300 font-medium flex items-center gap-1"
                        title={`${analytics.refunded_count || 0} refunded ${analytics.refunded_count === 1 ? 'invoice' : 'invoices'} on file (lifetime)`}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Refunds: ₹{Number(analytics.refunded_amount).toLocaleString('en-IN')}
                        <span className="opacity-70">({analytics.refunded_count || 0})</span>
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card data-testid="kpi-pending" className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200">
                  <CardContent className="p-3">
                    {/* Phase 273b — use the clean pending_amount + overdue_amount keys.
                        The old `total - paid` formula now over-counts because refunded amounts
                        are no longer inside `paid_fees`. */}
                    <p className="text-xl font-display font-bold tabular-nums text-amber-700">{formatINRCompact((analytics.pending_amount || 0) + (analytics.overdue_amount || 0))}</p>
                    <p className="text-[10px] text-amber-600 font-medium">Pending</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-blue-700">{analytics.collection_rate}%</p><p className="text-[10px] text-blue-600 font-medium">Collection Rate</p></CardContent></Card>
                <Card data-testid="kpi-overdue" className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-200">
                  <CardContent className="p-3">
                    {/* Phase 273c — show money-at-risk, not just row count. Falls back to
                        "0 invoices" gracefully when no overdue rows exist. */}
                    <p className="text-xl font-display font-bold tabular-nums text-red-700">{formatINRCompact(analytics.overdue_amount || 0)}</p>
                    <p className="text-[10px] text-red-600 font-medium">
                      Overdue · <span className="font-semibold">{analytics.overdue_count || 0}</span> {(analytics.overdue_count || 0) === 1 ? 'invoice' : 'invoices'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
            {/* Phase 258b — Bulk Reminders super-cell */}
            <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-rose-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                  <BellRing className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">One-Click Dunning Console</p>
                  <p className="text-xs text-muted-foreground">Fire tokenized Pay-Now reminders on every overdue / pending fee. Filter by batch or sport.</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  setBulkReminderOpen(true);
                  setBulkPreview(null);
                  setBulkResult(null);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="bulk-reminder-open"
              >
                <Send className="h-4 w-4 mr-1.5" /> Bulk Reminders
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {/* Phase 273 — Fee ledger filter / search / sort toolbar */}
                <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-slate-50/60 dark:bg-slate-900/30" data-testid="fee-ledger-toolbar">
                  <Input
                    value={feeTableSearch}
                    onChange={(e) => setFeeTableSearch(e.target.value)}
                    placeholder="Search by student or Fee ID…"
                    className="h-8 text-xs flex-1 min-w-[180px] max-w-[280px] bg-white dark:bg-slate-950"
                    data-testid="fee-ledger-search"
                  />
                  <Select value={feeStatusFilter} onValueChange={setFeeStatusFilter}>
                    <SelectTrigger className="h-8 text-xs w-[140px] bg-white dark:bg-slate-950" data-testid="fee-ledger-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="fee-filter-all">All statuses</SelectItem>
                      <SelectItem value="paid" data-testid="fee-filter-paid">Paid</SelectItem>
                      <SelectItem value="pending" data-testid="fee-filter-pending">Pending</SelectItem>
                      <SelectItem value="overdue" data-testid="fee-filter-overdue">Overdue</SelectItem>
                      <SelectItem value="refunded" data-testid="fee-filter-refunded">Refunded</SelectItem>
                      <SelectItem value="draft" data-testid="fee-filter-draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={feeSortKey} onValueChange={setFeeSortKey}>
                    <SelectTrigger className="h-8 text-xs w-[180px] bg-white dark:bg-slate-950" data-testid="fee-ledger-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="due_date_desc">Due date · newest first</SelectItem>
                      <SelectItem value="due_date_asc">Due date · oldest first</SelectItem>
                      <SelectItem value="amount_desc">Amount · high → low</SelectItem>
                      <SelectItem value="amount_asc">Amount · low → high</SelectItem>
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className="text-[10px] ml-auto" data-testid="fee-ledger-count-pill">
                    {displayedFees.length} of {fees.length}
                  </Badge>
                  {(feeTableSearch || feeStatusFilter !== 'all' || feeSortKey !== 'due_date_desc') && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => { setFeeTableSearch(''); setFeeStatusFilter('all'); setFeeSortKey('due_date_desc'); }}
                      data-testid="fee-ledger-reset"
                      className="h-7 text-[11px] px-2"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead className="w-28">Fee ID</TableHead><TableHead>Student</TableHead><TableHead>Amount</TableHead><TableHead className="whitespace-nowrap">Type</TableHead><TableHead className="whitespace-nowrap">Due Date</TableHead><TableHead>Status</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {fees.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No fees yet. Click "Generate Fees" to create.</TableCell></TableRow>
                    ) : displayedFees.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-10 text-xs text-muted-foreground" data-testid="fee-ledger-empty">
                        No invoices match your active filters.
                      </TableCell></TableRow>
                    ) : displayedFees.map((fee) => (
                      <TableRow key={fee.id}>
                        <TableCell><span className="text-xs font-mono text-muted-foreground">{fee.custom_id || '-'}</span></TableCell>
                        <TableCell className="font-medium text-sm">{fee.student_name || '-'}</TableCell>
                        <TableCell className="font-semibold tabular-nums">₹{fee.amount.toLocaleString()}</TableCell>
                        <TableCell className="whitespace-nowrap"><Badge variant="outline" className="text-xs">{fee.fee_type}</Badge></TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{formatDate(fee.due_date)}</TableCell>
                        <TableCell><FeeStatusBadge status={fee.status} refundStatus={fee.refund_status} /></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View Invoice" onClick={() => openSignedDocInNewTab(
                              `/fees/${fee.id}/view`,
                              { label: 'Invoice', errorMessage: 'Failed to load' },
                            )}>
                              <Eye className="h-3.5 w-3.5 text-blue-600" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Download PDF" onClick={async () => {
                              await downloadDocument(
                                api,
                                `/fees/${fee.id}/download`,
                                `${fee.custom_id || 'fee'}.pdf`,
                                { label: 'Fee Invoice' }
                              );
                            }}>
                              <Download className="h-3.5 w-3.5 text-emerald-600" />
                            </Button>
                            {/* Phase 281 — Email magic-link to parent / athlete. */}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              title="Email magic-link"
                              onClick={() => setEmailFeeRow(fee)}
                              data-testid={`fee-email-${fee.id}`}
                            >
                              <Mail className="h-3.5 w-3.5 text-indigo-600" />
                            </Button>
                            {fee.status !== 'paid' ? (
                              <>
                                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handlePayFeeOnline(fee)} disabled={payingFee === fee.id} data-testid="pay-online-button">
                                  {payingFee === fee.id ? <Clock className="h-3 w-3 animate-spin" /> : <><CreditCard className="h-3 w-3 mr-1" /> Pay Online</>}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handlePayFee(fee)} data-testid="mark-paid-button">
                                  <CheckCircle className="h-3 w-3 mr-1" /> Mark Paid
                                </Button>
                                {/* Phase 257 — Edit/Delete dropdown for pending fees */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" data-testid={`fee-row-menu-${fee.id}`} aria-label="More fee actions">
                                      <MoreVertical className="h-3.5 w-3.5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem onClick={() => { setReminderFee(fee); setReminderChannels({ email: true, whatsapp: true }); }} data-testid={`fee-remind-${fee.id}`}>
                                      <BellRing className="h-3.5 w-3.5 mr-2 text-amber-500" /> Send Reminder
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setEditFee({ ...fee })} data-testid={`fee-edit-${fee.id}`}>
                                      <Edit className="h-3.5 w-3.5 mr-2" /> Edit Bill
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDeleteFee(fee)} className="text-rose-600 focus:text-rose-700" data-testid={`fee-delete-${fee.id}`}>
                                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Bill
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-7 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" 
                                onClick={() => { setRefundFee(fee); setRefundForm({ amount: fee.amount.toString(), reason: '', offline: false, offline_method: 'cash_or_upi' }); }}
                                disabled={fee.refund_status === 'processing' || fee.refund_status === 'refunded'}
                                data-testid="refund-button"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                {fee.refund_status === 'processing' ? 'Refunding...' : fee.refund_status === 'refunded' ? 'Refunded' : 'Refund'}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Progress Cards Tab */}
        <TabsContent value="progress">
          <div className="space-y-4">
            {/* Progress Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-purple-700">{progressCards.length}</p><p className="text-[10px] text-purple-600 font-medium">Cards Generated</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-emerald-700">{progressCards.length > 0 ? (Object.values(progressCards[0]?.skills_rating || {}).reduce((a,b) => a+b, 0) / Math.max(Object.keys(progressCards[0]?.skills_rating || {}).length, 1)).toFixed(1) : '0'}/5</p><p className="text-[10px] text-emerald-600 font-medium">Avg Rating</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-blue-700">{students.length - progressCards.length}</p><p className="text-[10px] text-blue-600 font-medium">Pending Assessment</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-amber-700">{students.length}</p><p className="text-[10px] text-amber-600 font-medium">Total Students</p></CardContent></Card>
            </div>
            <div className="flex justify-end">
              <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
                <DialogTrigger asChild>
                  <Button size="sm"><Award className="h-4 w-4 mr-1" /> Add Progress Card</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Progress Card</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddProgressCard} className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Student *</Label>
                      <Select value={progressForm.student_id} onValueChange={(v) => setProgressForm({...progressForm, student_id: v, skills_rating: {}, sport_override: '' /* reset ratings + sport override when student changes — new sport may have different metrics */})}>
                        <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                        <SelectContent>{students.filter(s => s.status === 'active').map(s => <SelectItem key={s.id} value={s.id}>{s.custom_id} - {s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {/* Phase 98 → 98.c — Resolve a sport-specific metric matrix from the selected
                        student's enrolled sport / course (falls back to generic when no student
                        is selected, sport is unknown, OR student has no sport tagged — in which
                        case the coach can pick one via the override dropdown). */}
                    {(() => {
                      const selectedStudent = students.find(s => s.id === progressForm.student_id);
                      // Phase 98.c — corrected lookup chain. `course` now correctly takes
                      // precedence over `batch` (which contains time-of-day like "Morning").
                      const resolvedSport = selectedStudent?.sport || selectedStudent?.course || '';
                      const activeSport = progressForm.sport_override || resolvedSport;
                      const matrix = getSportMatrix(activeSport);
                      const isFallback = matrix.isFallback === true;
                      return (
                        <>
                          {isFallback && progressForm.student_id ? (
                            <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px]" data-testid="progress-sport-fallback-banner">
                              <div className="flex items-start gap-1.5 text-amber-800 dark:text-amber-200">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <div>
                                  <p className="font-bold">Generic matrix · sport not tagged</p>
                                  <p className="text-amber-700 dark:text-amber-300 text-[10px]">Pick a sport below to use specialised metrics for this evaluation.</p>
                                </div>
                              </div>
                              <select
                                value={progressForm.sport_override}
                                onChange={(e) => setProgressForm({...progressForm, sport_override: e.target.value, skills_rating: {}})}
                                className="bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-600 text-slate-800 dark:text-slate-100 px-2 py-1 rounded-md font-bold text-[11px]"
                                data-testid="progress-sport-override-select"
                              >
                                <option value="">Tag Student Sport →</option>
                                {AVAILABLE_SPORTS_LIST.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div className="rounded-md bg-primary/5 border border-primary/15 px-2.5 py-1.5 text-[11px] text-primary flex items-center justify-between" data-testid="progress-sport-banner">
                              <span><span className="font-semibold">{matrix.sport}</span> evaluation matrix</span>
                              {progressForm.sport_override
                                ? <span className="opacity-60">manual override</span>
                                : (resolvedSport ? <span className="opacity-60">from "{resolvedSport}"</span> : <span className="opacity-60">pick a student to specialise</span>)
                              }
                            </div>
                          )}
                          {matrix.skills.map(skill => (
                            <div key={skill.id} className="flex items-center gap-3" data-testid={`progress-skill-row-${skill.id}`}>
                              <Label className="text-xs flex-1 truncate">{skill.label}</Label>
                              <Input type="number" min="0" max="5" step="0.5" placeholder="0-5" className="w-20"
                                value={progressForm.skills_rating[skill.id] || ''}
                                onChange={(e) => setProgressForm({...progressForm, skills_rating: {...progressForm.skills_rating, [skill.id]: e.target.value}})}
                                data-testid={`progress-skill-input-${skill.id}`} />
                              <div className="flex gap-0.5">{Array.from({length: 5}).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < (progressForm.skills_rating[skill.id] || 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />)}</div>
                            </div>
                          ))}
                        </>
                      );
                    })()}
                    <div className="space-y-1.5"><Label className="text-xs">Coach Comments</Label><Input value={progressForm.comments} onChange={(e) => setProgressForm({...progressForm, comments: e.target.value})} placeholder="Performance notes..." /></div>
                    <Button type="submit" className="w-full">Save Progress Card</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Student Progress Cards</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Click on a card below to view detailed Digital Athlete ID</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {progressCards.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No progress cards yet. Add your first assessment.</div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {progressCards.map((card) => {
                      const student = students.find(s => s.id === card.student_id);
                      if (!student) return null;
                      
                      return (
                        <div key={card.id} className="cursor-pointer" onClick={() => {
                          // Show Digital Athlete Card in a dialog or modal
                          const cardDialog = document.createElement('div');
                          cardDialog.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.8);z-index:9999;display:flex;align-items:center;justify-content:center;padding:20px';
                          cardDialog.innerHTML = `<div style="max-width:900px;width:100%;max-height:90vh;overflow-y:auto" id="athlete-card-container"></div>`;
                          cardDialog.onclick = (e) => { if (e.target === cardDialog) document.body.removeChild(cardDialog); };
                          document.body.appendChild(cardDialog);
                          
                          // Render DigitalAthleteCard would go here (requires React portal for proper implementation)
                          toast.info('Full Digital Athlete Card view - implementation in progress');
                        }}>
                          <Card className="hover:shadow-lg transition-shadow border-2 hover:border-primary/50">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3 mb-3">
                                {student.photo_url ? (
                                  <img src={student.photo_url} alt={student.name} className="h-12 w-12 rounded-full object-cover" />
                                ) : (
                                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                                    {student.name?.charAt(0)}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{card.student_name}</p>
                                  <p className="text-xs font-mono text-muted-foreground">{student.custom_id}</p>
                                  <div className="flex gap-1 mt-1">
                                    <Badge variant="outline" className="text-[10px]">{student.course || student.sport}</Badge>
                                    <Badge variant="outline" className="text-[10px]">{student.batch}</Badge>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {Object.entries(card.skills_rating || {}).slice(0, 3).map(([skill, rating]) => (
                                  <Badge key={skill} className="text-[10px] bg-teal-50 text-teal-700">
                                    {skill}: {rating}/5
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex items-center justify-between pt-2 border-t">
                                <span className="text-xs text-muted-foreground">{card.coach_name || 'Coach'}</span>
                                <span className="text-xs text-muted-foreground">{new Date(card.created_at).toLocaleDateString()}</span>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>


      {/* Edit Student Dialog */}
      <Dialog open={!!editStudent} onOpenChange={() => setEditStudent(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Student</DialogTitle></DialogHeader>
          {editStudent && <StudentForm data={editStudent} setData={setEditStudent} onSubmit={handleEditStudent} submitLabel="Save Changes" courses={availableCourses} />}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog — Phase 95 two-step flow */}
      <Dialog open={!!resetPwStudent} onOpenChange={(open) => { if (!open) { setResetPwStudent(null); setResetPwForm({ password: '', confirm: '' }); setResetCompleted(false); setSharingChannel(null); } }}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-primary" /> Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="font-medium">{resetPwStudent?.name}</p>
              <p className="text-xs text-muted-foreground">{resetPwStudent?.custom_id} — {resetPwStudent?.email || 'No email'}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">New Password</Label>
              <Input
                type="password"
                placeholder="Min 6 characters"
                value={resetPwForm.password}
                onChange={(e) => setResetPwForm({...resetPwForm, password: e.target.value})}
                disabled={resetCompleted}
                data-testid="reset-password-new"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Confirm Password</Label>
              <Input
                type="password"
                placeholder="Re-enter password"
                value={resetPwForm.confirm}
                onChange={(e) => setResetPwForm({...resetPwForm, confirm: e.target.value})}
                disabled={resetCompleted}
                data-testid="reset-password-confirm"
              />
            </div>
            {resetPwForm.password && resetPwForm.confirm && resetPwForm.password !== resetPwForm.confirm && (
              <p className="text-xs text-destructive">Passwords do not match</p>
            )}

            {/* Step 1 — Reset & Copy. Disabled after completion. */}
            {!resetCompleted ? (
              <Button
                className="w-full"
                onClick={handleResetAndCopy}
                disabled={!resetPwForm.password || resetPwForm.password !== resetPwForm.confirm}
                data-testid="reset-password-submit"
              >
                <Key className="h-4 w-4 mr-2" /> Reset &amp; Copy Credentials
              </Button>
            ) : (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20 p-3 text-xs">
                <p className="font-semibold text-emerald-700 dark:text-emerald-300 mb-1 flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4" /> Password reset · copied to clipboard
                </p>
                <p className="text-emerald-600 dark:text-emerald-400 leading-snug">
                  Now choose how to share the new credentials with the student.
                </p>
              </div>
            )}

            {/* Step 2 — Channel-specific dispatch. Only active after reset. */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                disabled={!resetCompleted || sharingChannel === 'whatsapp' || !resetPwStudent?.phone}
                title={!resetPwStudent?.phone ? 'Student has no phone on file' : 'Send password via WhatsApp'}
                onClick={() => handleShareCredentials('whatsapp')}
                data-testid="reset-share-whatsapp"
              >
                {sharingChannel === 'whatsapp'
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Share2 className="h-3 w-3 mr-1" />}
                {sharingChannel === 'whatsapp' ? 'Sending…' : 'WhatsApp'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                disabled={!resetCompleted || sharingChannel === 'email' || !resetPwStudent?.email}
                title={!resetPwStudent?.email ? 'Student has no email on file' : 'Send password via email'}
                onClick={() => handleShareCredentials('email')}
                data-testid="reset-share-email"
              >
                {sharingChannel === 'email'
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Share2 className="h-3 w-3 mr-1" />}
                {sharingChannel === 'email' ? 'Sending…' : 'Email'}
              </Button>
            </div>
            {resetCompleted && (
              <p className="text-[10px] text-muted-foreground text-center -mt-1">
                Click the × icon when you're done sharing.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Parent Dialog */}
      <Dialog open={!!inviteParentStudent} onOpenChange={() => { setInviteParentStudent(null); setInviteParentForm({ email: '', name: '', phone: '', password: '' }); }}>
        <DialogContent className="max-w-sm" data-testid="invite-parent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> Invite Parent</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="font-medium">{inviteParentStudent?.name}</p>
              <p className="text-xs text-muted-foreground">{inviteParentStudent?.custom_id}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Parent Email *</Label>
              <Input type="email" placeholder="parent@example.com" value={inviteParentForm.email} onChange={(e) => setInviteParentForm({...inviteParentForm, email: e.target.value})} data-testid="invite-parent-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Parent Name</Label>
              <Input placeholder={inviteParentStudent?.guardian_name || 'Parent name'} value={inviteParentForm.name} onChange={(e) => setInviteParentForm({...inviteParentForm, name: e.target.value})} data-testid="invite-parent-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone (for SMS/WhatsApp invite)</Label>
              <Input placeholder={inviteParentStudent?.guardian_phone || '+91...'} value={inviteParentForm.phone} onChange={(e) => setInviteParentForm({...inviteParentForm, phone: e.target.value})} data-testid="invite-parent-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Temp Password</Label>
              <Input placeholder="Parent@1234 (default)" value={inviteParentForm.password} onChange={(e) => setInviteParentForm({...inviteParentForm, password: e.target.value})} />
            </div>
            <Button className="w-full" onClick={handleInviteParent} disabled={invitingParent || !inviteParentForm.email} data-testid="invite-parent-submit">
              {invitingParent ? 'Inviting...' : 'Send Invite'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 101 — Document Composer + Ledger */}
      <DocumentComposer
        open={!!composeForStudent}
        onClose={() => setComposeForStudent(null)}
        targetType="student"
        target={composeForStudent}
        academy={myAcademy}
        onSaved={() => setComposeForStudent(null)}
      />
      <DocumentLedger
        open={!!ledgerForStudent}
        onClose={() => setLedgerForStudent(null)}
        targetType="student"
        target={ledgerForStudent}
        academy={myAcademy}
      />

      {/* Student Detail Dialog - Enhanced with Charts */}
      <Dialog open={!!viewStudent} onOpenChange={() => { setViewStudent(null); setStudentHistory(null); activeHistoryStudentIdRef.current = null; }}>
        <DialogContent className="w-[95vw] sm:w-full max-w-3xl mx-auto max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col p-4 sm:p-6" data-testid="student-detail-dialog">
          <DialogHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                {/* Phase 88 — Identity photo in modal header */}
                <Avatar src={bestPhotoFor(viewStudent)} name={viewStudent?.name} size={40} tone="student" ring="ring-2 ring-emerald-100 dark:ring-emerald-900" testId="student-modal-avatar" />
                <div className="min-w-0">
                  <DialogTitle className="flex items-center gap-2 text-base sm:text-lg truncate">
                    <span className="truncate">{viewStudent?.name}</span>
                    <span className="text-xs font-mono text-muted-foreground hidden sm:inline">{viewStudent?.custom_id}</span>
                  </DialogTitle>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px] sm:text-xs">{viewStudent?.sport}</Badge>
                    <Badge variant="secondary" className="text-[10px] sm:text-xs">{viewStudent?.batch}</Badge>
                    {viewStudent?.bmi && <Badge variant="secondary" className="text-[10px] sm:text-xs">BMI: {viewStudent.bmi}</Badge>}
                    {viewStudent?.blood_group && <Badge variant="secondary" className="text-[10px] sm:text-xs">{viewStudent.blood_group}</Badge>}
                  </div>
                </div>
              </div>
              {studentHistory && (
                <div className="flex gap-2 sm:shrink-0">
                  <div className="flex-1 sm:flex-none text-center px-3 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-base sm:text-lg font-display font-bold text-emerald-600 tabular-nums">{studentHistory.attendance.rate}%</p>
                    <p className="text-[10px] text-muted-foreground">Attendance</p>
                  </div>
                  <div className="flex-1 sm:flex-none text-center px-3 py-1 rounded-lg bg-primary/5">
                    <p className="text-base sm:text-lg font-display font-bold tabular-nums">₹{(studentHistory.fees.paid/1000).toFixed(0)}K</p>
                    <p className="text-[10px] text-muted-foreground">Paid</p>
                  </div>
                </div>
              )}
            </div>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : studentHistory ? (
            <Tabs defaultValue="attendance" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="w-full">
                <TabsTrigger value="attendance" className="flex-1"><CalendarCheck className="h-3.5 w-3.5 mr-1" /> Attendance</TabsTrigger>
                <TabsTrigger value="fees" className="flex-1"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Fees</TabsTrigger>
                <TabsTrigger value="progress" className="flex-1"><Award className="h-3.5 w-3.5 mr-1" /> Progress</TabsTrigger>
              </TabsList>
              <TabsContent value="attendance" className="flex-1 overflow-auto space-y-3">
                {/* Attendance Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center"><p className="text-sm font-bold text-emerald-600 tabular-nums">{studentHistory.attendance.present}</p><p className="text-[10px] text-muted-foreground">Present</p></div>
                  <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-center"><p className="text-sm font-bold text-red-600 tabular-nums">{studentHistory.attendance.total - studentHistory.attendance.present}</p><p className="text-[10px] text-muted-foreground">Absent/Late</p></div>
                  <div className="p-2 rounded-lg bg-primary/5 text-center"><p className="text-sm font-bold tabular-nums">{studentHistory.attendance.rate}%</p><p className="text-[10px] text-muted-foreground">Rate</p></div>
                </div>
                {/* Attendance Chart */}
                {studentHistory.attendance.records.length > 1 && (
                  <div className="h-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={studentHistory.attendance.records.slice().reverse().map((r, i) => ({
                        date: r.date.slice(5),
                        value: r.status === 'present' ? 1 : r.status === 'late' ? 0.5 : 0,
                        cumRate: Math.round(studentHistory.attendance.records.slice(0, studentHistory.attendance.records.length - i).filter(x => x.status === 'present').length / (studentHistory.attendance.records.length - i) * 100)
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 22%, 88%)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={(v) => v + '%'} />
                        <Tooltip formatter={(value) => [value + '%', 'Attendance Rate']} />
                        <Line type="monotone" dataKey="cumRate" stroke="#0f766e" strokeWidth={2} dot={{ r: 3, fill: '#0f766e' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Attendance Records */}
                <div className="max-h-32 overflow-auto overflow-x-auto rounded-lg border border-slate-100">
                  <Table><TableHeader><TableRow><TableHead className="text-xs whitespace-nowrap">Date</TableHead><TableHead className="text-xs whitespace-nowrap">Status</TableHead><TableHead className="text-xs whitespace-nowrap">Method</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {studentHistory.attendance.records.slice(0, 15).map((a, i) => (
                        <TableRow key={i}><TableCell className="text-xs py-1.5 whitespace-nowrap">{formatDate(a.date)}</TableCell><TableCell className="whitespace-nowrap"><Badge variant={a.status === 'present' ? 'default' : a.status === 'late' ? 'secondary' : 'destructive'} className="text-[10px]">{a.status}</Badge></TableCell><TableCell className="text-xs text-muted-foreground whitespace-nowrap">{a.method || 'manual'}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              <TabsContent value="fees" className="flex-1 overflow-auto space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center"><p className="text-sm font-bold text-emerald-600 tabular-nums">₹{studentHistory.fees.paid.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Paid</p></div>
                  <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center"><p className="text-sm font-bold text-amber-600 tabular-nums">₹{studentHistory.fees.pending.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Pending</p></div>
                  <div className="p-2 rounded-lg bg-primary/5 text-center"><p className="text-sm font-bold tabular-nums">₹{studentHistory.fees.total.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Total</p></div>
                </div>
                {/* Phase 289 — Empty-state fallback with inline single-fee mint.
                    Replaces the dead "empty table under headers" with an
                    actionable CTA so the academy owner can patch the missing
                    invoice in one click without leaving this modal. */}
                {studentHistory.fees.records.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white dark:from-emerald-950/30 dark:to-slate-900 p-4 sm:p-5 space-y-3" data-testid="student-fees-empty-state">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <Receipt className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">No billing history found for this student</p>
                        <p className="text-[11px] text-slate-500 leading-snug mt-0.5">They were likely added after the monthly batch ran or have no pricing tier assigned. Generate their current-month fee below.</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
                      <div className="flex-1 space-y-1.5">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Amount (₹)</Label>
                        <Input
                          type="number" min="0" step="0.01" inputMode="decimal"
                          placeholder="e.g. 2500"
                          value={quickFeeAmount}
                          onChange={(e) => setQuickFeeAmount(e.target.value)}
                          className="font-mono"
                          data-testid="quick-fee-amount-input"
                        />
                      </div>
                      <Button
                        onClick={handleQuickGenerateFee}
                        disabled={quickFeeSaving || !quickFeeAmount}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                        data-testid="quick-fee-generate-btn"
                      >
                        {quickFeeSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <IndianRupee className="h-4 w-4 mr-1" />}
                        Generate Current Month Fee
                      </Button>
                    </div>
                    {(viewStudent?.final_fee || viewStudent?.base_fee) && (
                      <p className="text-[10px] text-slate-500 italic">Pre-filled from this student's saved fee profile (Phase 245). Edit if needed.</p>
                    )}
                  </div>
                ) : (
                  <div className="max-h-48 overflow-auto overflow-x-auto rounded-lg border border-slate-100">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs whitespace-nowrap">Fee ID</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Amount</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Due</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {studentHistory.fees.records.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs font-mono whitespace-nowrap">{f.custom_id || '-'}</TableCell>
                            <TableCell className="text-xs font-semibold tabular-nums whitespace-nowrap">₹{f.amount.toLocaleString()}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{formatDate(f.due_date)}</TableCell>
                            <TableCell className="whitespace-nowrap"><FeeStatusBadge status={f.status} refundStatus={f.refund_status} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="progress" className="flex-1 overflow-auto space-y-3">
                {studentHistory.progress.length > 0 && studentHistory.progress[0].skills_rating && (
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={Object.entries(studentHistory.progress[0].skills_rating).map(([name, value]) => ({ skill: name, value, fullMark: 5 }))}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="skill" tick={{ fontSize: 10 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fontSize: 9 }} />
                        <Radar name="Skills" dataKey="value" stroke="#0f766e" fill="#0f766e" fillOpacity={0.3} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Phase 197-H — AI Coach's Training Blueprint */}
                {studentHistory.progress.length > 0 && viewStudent?.id && (
                  <StudentAIRecommendationCard studentId={viewStudent.id} compact />
                )}
                {/* Phase 197.b — Trend AI longitudinal growth trajectory */}
                {studentHistory.progress.length >= 3 && viewStudent?.id && (
                  <AthleteTrajectoryCard studentId={viewStudent.id} />
                )}
                {studentHistory.progress.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No progress cards yet</p>
                ) : (
                  <div className="space-y-2 max-h-36 overflow-auto">
                    {studentHistory.progress.map((p, i) => (
                      <div key={i} className="p-2.5 rounded-lg border">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium">{p.coach_name || 'Coach'}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">{Object.entries(p.skills_rating || {}).map(([s, v]) => <Badge key={s} variant="outline" className="text-[10px]">{s}: {v}/5</Badge>)}</div>
                        {p.comments && <p className="text-[10px] text-muted-foreground mt-1 italic">"{p.comments}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>


      {/* Phase 202.iso-date — Record Offline Payment Dialog */}
      <Dialog open={!!offlinePayFee} onOpenChange={(o) => { if (!o) setOfflinePayFee(null); }}>
        <DialogContent className="max-w-md" data-testid="offline-pay-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-5 w-5 text-emerald-600" />
              Record Offline Payment
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Capture mode, amount, and reference so the academy ledger has the full audit trail.
            </DialogDescription>
          </DialogHeader>
          {offlinePayFee && (
            <form onSubmit={handleConfirmOfflinePayment} className="space-y-4 pt-1">
              <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 text-xs">
                <div className="text-slate-500">Student</div>
                <div className="font-semibold text-slate-900">{offlinePayFee.student_name || offlinePayFee.student_id}</div>
                <div className="mt-1 text-slate-500">Invoice</div>
                <div className="font-mono text-slate-700">{offlinePayFee.custom_id || offlinePayFee.id?.slice(0, 8)}</div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Mode of Payment</Label>
                <Select value={offlinePayForm.mode} onValueChange={(v) => setOfflinePayForm({ ...offlinePayForm, mode: v })}>
                  <SelectTrigger data-testid="offline-pay-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI (GPay / PhonePe / Paytm)</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer / NEFT</SelectItem>
                    <SelectItem value="Card">Card Swipe</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Amount Received (₹)</Label>
                  <Input
                    type="number" min="0" step="0.01" required
                    value={offlinePayForm.amount}
                    onChange={(e) => setOfflinePayForm({ ...offlinePayForm, amount: e.target.value })}
                    data-testid="offline-pay-amount-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Payment Date</Label>
                  <Input
                    type="date" required
                    value={offlinePayForm.payment_date}
                    onChange={(e) => setOfflinePayForm({ ...offlinePayForm, payment_date: e.target.value })}
                    data-testid="offline-pay-date-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Reference / Transaction Number <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  type="text" placeholder="UPI ref · Bank txn ID · Cheque #"
                  value={offlinePayForm.transaction_id}
                  onChange={(e) => setOfflinePayForm({ ...offlinePayForm, transaction_id: e.target.value })}
                  className="font-mono text-xs"
                  data-testid="offline-pay-ref-input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setOfflinePayFee(null)} data-testid="offline-pay-cancel">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingOfflinePay} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="offline-pay-confirm">
                  {savingOfflinePay ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Banknote className="h-4 w-4 mr-1" /> Settle Balance</>}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>


      {/* Phase 257 — Edit Pending Fee Bill */}
      <Dialog open={!!editFee} onOpenChange={(o) => { if (!o) setEditFee(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-fee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5 text-emerald-600" /> Edit Fee Bill</DialogTitle>
          </DialogHeader>
          {editFee && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 border text-xs space-y-0.5">
                <p className="font-medium">{editFee.student_name}</p>
                <p className="text-muted-foreground">Invoice: <span className="font-mono">{editFee.custom_id || editFee.id?.slice(0, 8)}</span> · {editFee.description || 'Fee'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Amount (₹)</label>
                <Input
                  type="number"
                  value={editFee.amount ?? ''}
                  onChange={(e) => setEditFee(prev => ({ ...prev, amount: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                  className="font-mono"
                  data-testid="edit-fee-amount"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Due Date</label>
                <Input
                  type="date"
                  value={editFee.due_date || ''}
                  onChange={(e) => setEditFee(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="edit-fee-due-date"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditFee(null)} disabled={feeMutating}>Cancel</Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={feeMutating || editFee.amount === '' || !editFee.amount}
                  data-testid="edit-fee-save"
                  onClick={async () => {
                    setFeeMutating(true);
                    try {
                      await api.put(`/fees/${editFee.id}`, {
                        amount: parseFloat(editFee.amount),
                        due_date: editFee.due_date || null,
                      });
                      toast.success('Fee bill updated');
                      setEditFee(null);
                      fetchFees?.();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to update fee');
                    } finally {
                      setFeeMutating(false);
                    }
                  }}
                >
                  {feeMutating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 257 — Delete Pending Fee Bill confirmation */}
      <Dialog open={!!deleteFee} onOpenChange={(o) => { if (!o) setDeleteFee(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-fee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Delete Fee Bill</DialogTitle>
          </DialogHeader>
          {deleteFee && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800 text-sm space-y-1">
                <p className="font-medium">{deleteFee.student_name}</p>
                <p className="text-xs text-muted-foreground">
                  Invoice: <span className="font-mono">{deleteFee.custom_id || deleteFee.id?.slice(0, 8)}</span>
                  &nbsp;·&nbsp; Amount: <span className="font-mono">₹{Number(deleteFee.amount || 0).toLocaleString()}</span>
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                This permanently removes the pending bill from the ledger. The student's <strong>Pending</strong> and <strong>Overdue</strong> totals will recalculate automatically. This cannot be undone.
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteFee(null)} disabled={feeMutating}>Cancel</Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  disabled={feeMutating}
                  data-testid="delete-fee-confirm"
                  onClick={async () => {
                    setFeeMutating(true);
                    try {
                      await api.delete(`/fees/${deleteFee.id}`);
                      toast.success('Fee bill deleted');
                      setDeleteFee(null);
                      fetchFees?.();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to delete fee');
                    } finally {
                      setFeeMutating(false);
                    }
                  }}
                >
                  {feeMutating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                  Delete Permanently
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 281 — Context-aware fee-invoice email magic-link dialog. */}
      <EmailDocumentDialog
        open={!!emailFeeRow}
        onOpenChange={(o) => { if (!o) setEmailFeeRow(null); }}
        documentType="invoice"
        docPath={emailFeeRow ? `/fees/${emailFeeRow.id}/view` : null}
        defaultEmail={emailFeeRow ? (
          students.find(s => s.id === emailFeeRow.student_id)?.parent_email
          || students.find(s => s.id === emailFeeRow.student_id)?.email
          || ''
        ) : ''}
        defaultName={emailFeeRow ? (emailFeeRow.student_name || students.find(s => s.id === emailFeeRow.student_id)?.parent_name || '') : ''}
        contextData={emailFeeRow ? (() => {
          // Phase 281 — Defensive period parser.  Fee `due_date` arrives in
          // multiple formats from legacy records (ISO, DD/MM/YYYY, blank).
          // Try ISO first; if that yields Invalid Date, attempt DD/MM/YYYY;
          // fall back to fee_type when both fail so the message never reads
          // "Invalid Date".
          const raw = emailFeeRow.due_date || emailFeeRow.payment_date || '';
          let period = '';
          if (raw) {
            let d = new Date(raw);
            if (isNaN(d.getTime())) {
              const m = String(raw).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
              if (m) d = new Date(`${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`);
            }
            if (!isNaN(d.getTime())) {
              period = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
            }
          }
          if (!period) period = emailFeeRow.fee_type || '';
          return {
            period,
            amount: `₹${Number(emailFeeRow.amount || 0).toLocaleString('en-IN')}`,
          };
        })() : {}}
      />

      {/* Phase 258 — Manual Multichannel Fee Reminder dialog */}
      <Dialog open={!!reminderFee} onOpenChange={(o) => { if (!o) { setReminderFee(null); setReminderSending(false); } }}>
        <DialogContent className="max-w-md" data-testid="fee-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing className="h-5 w-5 text-amber-500" /> Send Pay-Now Reminder</DialogTitle>
            <DialogDescription className="text-xs">
              A tokenized Razorpay checkout link will be dispatched. Parent can pay in one click without logging in (link valid 72h).
            </DialogDescription>
          </DialogHeader>
          {reminderFee && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-sm space-y-1">
                <p className="font-medium" data-testid="reminder-fee-student">{reminderFee.student_name}</p>
                <p className="text-xs text-muted-foreground">
                  Invoice: <span className="font-mono">{reminderFee.custom_id || reminderFee.id?.slice(0, 8)}</span>
                  &nbsp;·&nbsp; Amount: <span className="font-mono font-semibold">₹{Number(reminderFee.amount || 0).toLocaleString()}</span>
                  &nbsp;·&nbsp; Due: <span className="font-mono">{reminderFee.due_date || '—'}</span>
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Select Channels</p>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer">
                  <Checkbox
                    checked={reminderChannels.email}
                    onCheckedChange={(v) => setReminderChannels((p) => ({ ...p, email: !!v }))}
                    data-testid="reminder-channel-email"
                  />
                  <Mail className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">Email <span className="text-xs text-muted-foreground">(parent on file)</span></span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer">
                  <Checkbox
                    checked={reminderChannels.whatsapp}
                    onCheckedChange={(v) => setReminderChannels((p) => ({ ...p, whatsapp: !!v }))}
                    data-testid="reminder-channel-whatsapp"
                  />
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">WhatsApp <span className="text-xs text-muted-foreground">(guardian / student phone)</span></span>
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setReminderFee(null)} disabled={reminderSending}>Cancel</Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  disabled={reminderSending || (!reminderChannels.email && !reminderChannels.whatsapp)}
                  data-testid="reminder-send-confirm"
                  onClick={async () => {
                    const selected = Object.keys(reminderChannels).filter((k) => reminderChannels[k]);
                    if (selected.length === 0) {
                      toast.error('Please select at least one channel');
                      return;
                    }
                    setReminderSending(true);
                    try {
                      const res = await api.post(`/fees/${reminderFee.id}/remind`, { channels: selected });
                      const succeeded = res.data?.channels_succeeded || [];
                      const errors = res.data?.errors || {};
                      if (succeeded.length > 0) {
                        const labels = succeeded.map((c) => c === 'email' ? 'Email' : 'WhatsApp').join(' + ');
                        toast.success(`Pay-Now reminder sent via ${labels}`);
                      }
                      // Surface per-channel failure reasons (no_email_on_file, no_phone_on_file, etc.)
                      Object.entries(errors).forEach(([ch, reason]) => {
                        const chLabel = ch === 'email' ? 'Email' : 'WhatsApp';
                        const friendly = reason === 'no_email_on_file' ? 'no email on file'
                                       : reason === 'no_phone_on_file' ? 'no phone on file'
                                       : `failed (${String(reason).slice(0, 60)})`;
                        toast.error(`${chLabel}: ${friendly}`);
                      });
                      if (succeeded.length === 0 && Object.keys(errors).length === 0) {
                        toast.error('No channels could be dispatched');
                      }
                      setReminderFee(null);
                    } catch (err) {
                      apiErrorToast(err, 'Failed to send reminder');
                    } finally {
                      setReminderSending(false);
                    }
                  }}
                >
                  {reminderSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Send Reminder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Phase 258b — Bulk Multichannel Fee Reminder dialog */}
      <Dialog open={bulkReminderOpen} onOpenChange={(o) => { if (!o) { setBulkReminderOpen(false); setBulkPreview(null); setBulkResult(null); setBulkLoading(false); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing className="h-5 w-5 text-amber-500" /> Bulk Pay-Now Reminders</DialogTitle>
            <DialogDescription className="text-xs">
              Fires tokenized Razorpay checkout links to every matching unpaid fee. Each parent receives a one-click pay link (no login).
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-4">
              {/* Filter controls */}
              <div className="space-y-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Filters</p>

                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={bulkOnlyOverdue}
                    onCheckedChange={(v) => { setBulkOnlyOverdue(!!v); setBulkPreview(null); }}
                    data-testid="bulk-reminder-overdue"
                  />
                  <span className="text-sm">Only past-due fees <span className="text-xs text-muted-foreground">(due_date &lt; today)</span></span>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1">Batch (optional)</Label>
                    <select
                      value={bulkBatch}
                      onChange={(e) => { setBulkBatch(e.target.value); setBulkPreview(null); }}
                      className="w-full h-9 text-sm rounded-md border border-input bg-background px-2"
                      data-testid="bulk-reminder-batch"
                    >
                      <option value="">All batches</option>
                      {batchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1">Sport (optional)</Label>
                    <select
                      value={bulkSport}
                      onChange={(e) => { setBulkSport(e.target.value); setBulkPreview(null); }}
                      className="w-full h-9 text-sm rounded-md border border-input bg-background px-2"
                      data-testid="bulk-reminder-sport"
                    >
                      <option value="">All sports</option>
                      {courseOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Channels */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Channels</p>
                <label className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer">
                  <Checkbox
                    checked={bulkChannels.email}
                    onCheckedChange={(v) => setBulkChannels((p) => ({ ...p, email: !!v }))}
                    data-testid="bulk-channel-email"
                  />
                  <Mail className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer">
                  <Checkbox
                    checked={bulkChannels.whatsapp}
                    onCheckedChange={(v) => setBulkChannels((p) => ({ ...p, whatsapp: !!v }))}
                    data-testid="bulk-channel-whatsapp"
                  />
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">WhatsApp</span>
                </label>
              </div>

              {/* Preview pill */}
              {bulkPreview && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm" data-testid="bulk-reminder-preview">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    {bulkPreview.matched_count === 0
                      ? 'No matching unpaid fees found.'
                      : `${bulkPreview.capped_count} fee${bulkPreview.capped_count === 1 ? '' : 's'} will be notified.`}
                  </p>
                  {bulkPreview.matched_count > bulkPreview.capped_count && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      {bulkPreview.matched_count} matched · capped to first {bulkPreview.capped_count} (run again to dispatch the rest).
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setBulkReminderOpen(false)} disabled={bulkLoading}>Cancel</Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={bulkLoading}
                  data-testid="bulk-reminder-preview-btn"
                  onClick={async () => {
                    const selected = Object.keys(bulkChannels).filter((k) => bulkChannels[k]);
                    setBulkLoading(true);
                    try {
                      const res = await api.post('/fees/bulk-remind', {
                        channels: selected.length ? selected : ['email'],
                        only_overdue: bulkOnlyOverdue,
                        batch: bulkBatch || null,
                        sport: bulkSport || null,
                        dry_run: true,
                      });
                      setBulkPreview({ matched_count: res.data.matched_count, capped_count: res.data.capped_count });
                    } catch (err) {
                      apiErrorToast(err, 'Preview failed');
                    } finally {
                      setBulkLoading(false);
                    }
                  }}
                >
                  {bulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                  Preview Count
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  disabled={bulkLoading || (!bulkChannels.email && !bulkChannels.whatsapp) || (bulkPreview && bulkPreview.matched_count === 0)}
                  data-testid="bulk-reminder-send"
                  onClick={async () => {
                    const selected = Object.keys(bulkChannels).filter((k) => bulkChannels[k]);
                    if (selected.length === 0) {
                      toast.error('Select at least one channel');
                      return;
                    }
                    setBulkLoading(true);
                    try {
                      const res = await api.post('/fees/bulk-remind', {
                        channels: selected,
                        only_overdue: bulkOnlyOverdue,
                        batch: bulkBatch || null,
                        sport: bulkSport || null,
                        dry_run: false,
                      });
                      setBulkResult(res.data);
                    } catch (err) {
                      apiErrorToast(err, 'Bulk dispatch failed');
                    } finally {
                      setBulkLoading(false);
                    }
                  }}
                >
                  {bulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Send Now
                </Button>
              </div>
            </div>
          ) : (
            /* Result panel — per-student roster audit */
            <div className="space-y-3" data-testid="bulk-reminder-result">
              {/* Execution summary bar */}
              <div className="bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-2xl p-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-100">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>Relay Dispatch Complete</span>
                </div>
                <div className="flex items-center gap-2 font-mono font-bold text-[11px]" data-testid="bulk-reminder-result-counter">
                  <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900 px-2 py-0.5 rounded-md">
                    Success: {bulkResult.dispatched_count}
                  </span>
                  {(bulkResult.capped_count - bulkResult.dispatched_count) > 0 && (
                    <span className="bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-900 px-2 py-0.5 rounded-md">
                      Bypassed: {bulkResult.capped_count - bulkResult.dispatched_count}
                    </span>
                  )}
                </div>
              </div>

              {/* Per-student audit roster */}
              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-white dark:bg-slate-900/40">
                <div className="bg-slate-50/80 dark:bg-slate-900/60 px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-[9px] uppercase font-black tracking-wider text-slate-500 dark:text-slate-400">
                  Detailed Dispatch Audit Log
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-56 overflow-y-auto text-xs">
                  {(!bulkResult.results || bulkResult.results.length === 0) ? (
                    <div className="p-4 text-center text-slate-400 text-[11px]">No rows modified during execution run.</div>
                  ) : (
                    bulkResult.results.map((row) => {
                      const dispatched = (row.channels_succeeded || []).length > 0;
                      const errKeys = Object.keys(row.errors || {});
                      const friendlyReason = (() => {
                        if (errKeys.length === 0) return '';
                        // Prefer no_*_on_file reasons first
                        for (const k of errKeys) {
                          const v = row.errors[k];
                          if (v === 'no_email_on_file') return 'No email on file';
                          if (v === 'no_phone_on_file') return 'No phone on file';
                        }
                        const first = row.errors[errKeys[0]];
                        return `Delivery failed (${String(first).slice(0, 32)})`;
                      })();
                      const partialReason = dispatched && errKeys.length > 0 ? friendlyReason : '';
                      return (
                        <div key={row.fee_id} className="p-2.5 flex items-center justify-between gap-3 hover:bg-slate-50/40 dark:hover:bg-slate-800/40" data-testid={`bulk-row-${row.fee_id}`}>
                          <div className="space-y-0.5 truncate max-w-[60%]">
                            <div className="font-bold text-slate-800 dark:text-slate-100 truncate">{row.student_name}</div>
                            <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono tracking-tight truncate">
                              {row.custom_id || (row.fee_id || '').slice(0, 12)}
                              <span className="ml-1.5 text-slate-300 dark:text-slate-600">·</span>
                              <span className="ml-1.5">₹{Number(row.amount || 0).toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-0.5">
                            {dispatched ? (
                              <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900 text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1">
                                ● Dispatched ({(row.channels_succeeded || []).map(c => c === 'email' ? 'E' : 'W').join('+')})
                              </span>
                            ) : (
                              <span className="bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1" title={friendlyReason}>
                                <AlertTriangle className="w-3 h-3" /> {friendlyReason || 'Skipped'}
                              </span>
                            )}
                            {partialReason && (
                              <span className="text-[9px] text-amber-600 dark:text-amber-400 italic truncate max-w-[180px]" title={partialReason}>
                                partial: {partialReason.replace('No ', 'no ')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <Button className="w-full" onClick={() => { setBulkReminderOpen(false); setBulkPreview(null); setBulkResult(null); }} data-testid="bulk-reminder-close">Close Panel</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Refund Confirmation Dialog */}
      <Dialog open={!!refundFee} onOpenChange={() => { setRefundFee(null); setRefundForm({ amount: '', reason: '', offline: false, offline_method: 'cash_or_upi' }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-rose-600" />
              Initiate Refund
            </DialogTitle>
          </DialogHeader>
          {refundFee && (
            <div className="space-y-4">
              {/* Fee Summary */}
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{refundFee.student_name}</span>
                  <Badge variant="outline" className="text-xs">{refundFee.custom_id}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Fee Amount</span>
                  <span className="text-lg font-display font-bold tabular-nums">₹{refundFee.amount.toLocaleString()}</span>
                </div>
                {refundFee.payment_id && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Payment ID: <span className="font-mono">{refundFee.payment_id}</span>
                  </div>
                )}
              </div>

              {/* Warning — text swaps when offline settlement is selected */}
              <div className={`p-3 rounded-lg border ${refundForm.offline ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${refundForm.offline ? 'text-emerald-600' : 'text-amber-600'}`} />
                  <div className="flex-1">
                    {refundForm.offline ? (
                      <>
                        <p className="text-xs font-medium text-emerald-900 dark:text-emerald-100" data-testid="refund-warning-offline">Refund will be settled off-platform</p>
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">No funds move through Razorpay. The ledger row will be marked refunded with method = {refundForm.offline_method.replace('_', ' ')}. You are responsible for handing the money back via your chosen method.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-amber-900 dark:text-amber-100">Refund will be processed via Razorpay</p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">Funds will reach student's account in 5-7 business days. This action cannot be undone.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Phase 266i — Off-platform settlement toggle. Required for
                  mock-paid fees (no Razorpay txn to reverse), also valid
                  for real-paid fees when the academy refunds in cash at
                  the front desk. */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer" data-testid="refund-offline-toggle-label">
                  <input
                    type="checkbox"
                    checked={refundForm.offline}
                    onChange={(e) => setRefundForm({ ...refundForm, offline: e.target.checked })}
                    className="mt-0.5"
                    data-testid="refund-offline-toggle"
                  />
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">Settle off-platform (cash / UPI / bank transfer)</span>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      Required for mock-paid fees. Also use this when cash has already been returned at the desk and only the ledger needs updating.
                    </p>
                  </div>
                </label>

                {refundForm.offline && (
                  <div className="pl-6 space-y-1 pt-1">
                    <Label className="text-[10px] uppercase tracking-wide text-slate-500">Settlement method</Label>
                    <select
                      value={refundForm.offline_method}
                      onChange={(e) => setRefundForm({ ...refundForm, offline_method: e.target.value })}
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs w-full"
                      data-testid="refund-offline-method"
                    >
                      <option value="cash_or_upi">Cash or UPI</option>
                      <option value="cash">Cash</option>
                      <option value="upi">UPI direct</option>
                      <option value="bank_transfer">Bank transfer</option>
                      <option value="cheque">Cheque</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Refund Form */}
              <form onSubmit={handleInitiateRefund} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Refund Amount (₹) *</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={refundForm.amount} 
                    onChange={(e) => setRefundForm({...refundForm, amount: e.target.value})} 
                    placeholder={`Max: ${refundFee.amount}`}
                    max={refundFee.amount}
                    required 
                    data-testid="refund-amount-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave as is for full refund or enter partial amount</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Reason for Refund</Label>
                  <Input 
                    value={refundForm.reason} 
                    onChange={(e) => setRefundForm({...refundForm, reason: e.target.value})} 
                    placeholder="e.g., Student withdrew, duplicate payment, etc."
                    data-testid="refund-reason-input"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setRefundFee(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700" disabled={processingRefund} data-testid="confirm-refund-button">
                    {processingRefund ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><RotateCcw className="h-4 w-4 mr-2" /> Confirm Refund</>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Razorpay Setup Guide Dialog */}
      <Dialog open={showRazorpayGuide} onOpenChange={setShowRazorpayGuide}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CreditCard className="h-6 w-6 text-primary" />
              Enable Online Fee Payments
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5">
            {/* Alert Banner */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">Payment Gateway Not Configured</h4>
                  <p className="text-sm text-amber-800 dark:text-amber-200">To accept online fee payments from students, you need to configure your Razorpay payment gateway credentials.</p>
                </div>
              </div>
            </div>

            {/* Step-by-step Guide */}
            <div className="space-y-4">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs">1</div>
                Create Razorpay Account
              </h3>
              <div className="ml-8 space-y-2 text-sm text-muted-foreground">
                <p>• Visit <a href="https://dashboard.razorpay.com/signup" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">https://dashboard.razorpay.com/signup</a></p>
                <p>• Sign up with your academy email and phone number</p>
                <p>• Complete KYC verification (PAN, GST, Bank Account details)</p>
                <p className="text-xs text-amber-600">⚠️ Note: KYC approval takes 24-48 hours</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs">2</div>
                Get API Keys
              </h3>
              <div className="ml-8 space-y-2 text-sm text-muted-foreground">
                <p>• Log in to <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">Razorpay Dashboard</a></p>
                <p>• Go to <strong>Settings</strong> → <strong>API Keys</strong></p>
                <p>• Click <strong>"Generate Test Keys"</strong> (for testing) or <strong>"Generate Live Keys"</strong> (for production)</p>
                <p>• Copy both <strong>Key ID</strong> and <strong>Key Secret</strong></p>
                <div className="p-3 bg-muted rounded-lg text-xs font-mono">
                  <p className="text-emerald-600">Key ID: rzp_test_xxxxxxxxxxxxx</p>


      {/* Interactive Date Status Dialog */}
      <Dialog open={showDateStatusDialog} onOpenChange={setShowDateStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-primary" />
              Mark Attendance - {selectedDate}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm font-medium mb-1">
                {students.find(s => s.id === calendarStudent)?.name || 'Student'}
              </p>
              <p className="text-xs text-muted-foreground">
                Date: {selectedDate}
              </p>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Select Status:</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={dateDialogStatus === 'present' ? 'default' : 'outline'}
                  className={`h-16 flex-col gap-1 ${dateDialogStatus === 'present' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  onClick={() => setDateDialogStatus('present')}
                >
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-xs font-semibold">Present</span>
                </Button>

                <Button
                  type="button"
                  variant={dateDialogStatus === 'absent' ? 'default' : 'outline'}
                  className={`h-16 flex-col gap-1 ${dateDialogStatus === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => setDateDialogStatus('absent')}
                >
                  <XCircle className="h-5 w-5" />
                  <span className="text-xs font-semibold">Absent</span>
                </Button>

                <Button
                  type="button"
                  variant={dateDialogStatus === 'late' ? 'default' : 'outline'}
                  className={`h-16 flex-col gap-1 ${dateDialogStatus === 'late' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                  onClick={() => setDateDialogStatus('late')}
                >
                  <Clock className="h-5 w-5" />
                  <span className="text-xs font-semibold">Late</span>
                </Button>

                <Button
                  type="button"
                  variant={dateDialogStatus === 'leave' ? 'default' : 'outline'}
                  className={`h-16 flex-col gap-1 ${dateDialogStatus === 'leave' ? 'bg-slate-500 hover:bg-slate-600' : ''}`}
                  onClick={() => setDateDialogStatus('leave')}
                >
                  <Users className="h-5 w-5" />
                  <span className="text-xs font-semibold">Leave</span>
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1" 
                onClick={() => setShowDateStatusDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="button"
                className="flex-1 bg-primary" 
                onClick={handleSaveDateStatus}
                disabled={savingDateStatus}
              >
                {savingDateStatus ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark {dateDialogStatus.charAt(0).toUpperCase() + dateDialogStatus.slice(1)}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

                  <p className="text-rose-600 mt-1">Key Secret: xxxxxxxxxxxxxxxxxxxx</p>
                </div>
                <p className="text-xs text-rose-600">🔒 Keep Key Secret confidential - never share publicly!</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs">3</div>
                Configure in Out-Play Settings
              </h3>
              <div className="ml-8 space-y-2 text-sm text-muted-foreground">
                <p>• Go to <strong>Settings</strong> page in Out-Play</p>
                <p>• Scroll to <strong>"Payment Gateway Configuration"</strong> section</p>
                <p>• Paste your Razorpay <strong>Key ID</strong> and <strong>Key Secret</strong></p>
                <p>• Click <strong>"Save Settings"</strong></p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-base flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">✓</div>
                Start Accepting Payments
              </h3>
              <div className="ml-8 space-y-2 text-sm text-muted-foreground">
                <p>• Students can now pay fees online via UPI, Cards, Net Banking</p>
                <p>• Payments are deposited directly to your bank account (T+2 days)</p>
                <p>• Automatic fee status updates + email receipts sent</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={() => setShowRazorpayGuide(false)}>
                I'll Do This Later
              </Button>
              <Button className="flex-1 bg-primary" onClick={() => { setShowRazorpayGuide(false); window.location.href = '/settings'; }}>
                <CreditCard className="h-4 w-4 mr-2" />
                Go to Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Phase 84 — Compact student avatar (mirror of HRMS StaffAvatar) with photo or initials fallback.
// Phase 107 — Removed 3D sport-themed SVG fallback; profile imaging is now strictly
// `profile_photo_url` → real photo OR a clean dual-initials slate circle. If the photo
// URL exists but the image fails to load (broken/corrupt asset), we gracefully fall
// back to the same dual-initials circle so a stale row never shows a "blue blob".
function StudentAvatar({ student, size = 32 }) {
  const url = bestPhotoFor(student);
  const initials = (student?.name || '').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() || 'S';
  const px = `${size}px`;
  const [failed, setFailed] = useState(false);
  // Reset failure state when the underlying student/url changes
  useEffect(() => { setFailed(false); }, [url]);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={student?.name || ''}
        className="rounded-full object-cover border border-border shrink-0"
        style={{ width: px, height: px }}
        onError={() => setFailed(true)}
        data-testid={`student-avatar-${student?.id || 'x'}`}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-semibold shrink-0 border border-slate-200"
      style={{ width: px, height: px }}
      data-testid={`student-avatar-${student?.id || 'x'}`}
    >{initials}</div>
  );
}
